// ───────────────────────────────────────────────────────────────────────────────
// Imports
// ───────────────────────────────────────────────────────────────────────────────
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import * as SkeletonUtils from 'three/examples/jsm/utils/SkeletonUtils.js';
import gsap from 'gsap';

import treesVertexShader from "./shaders/trees/vertex.glsl";
import treesFragmentShader from "./shaders/trees/fragment.glsl";
import outlineVertexShader from "./shaders/outline/vertex.glsl";
import outlineFragmentShader from "./shaders/outline/fragment.glsl";

// ───────────────────────────────────────────────────────────────────────────────
/** Mapbox init */
// ───────────────────────────────────────────────────────────────────────────────
mapboxgl.accessToken = 'pk.eyJ1IjoiZHJpcHZpc3VhbCIsImEiOiJjbHZ6ZHgwc3kwMjJsMmtwaHM5dmJlcGM4In0.qImLeHGO5fmMHpEAihJV8w';


// Email intereset endpoint:
const INTEREST_ENDPOINT = 'https://publictwin.info/UtrechtDEPOT/php_scripts/sendInterest.php';

// one center to rule them all
const MAP_CENTER = [5.108830543530047, 52.075147667677925];


// ~500–600 m box around the center (tweak paddings to taste)
const MAX_BOUNDS = [
  [MAP_CENTER[0] - 0.0045, MAP_CENTER[1] - 0.0035], // SW
  [MAP_CENTER[0] + 0.0045, MAP_CENTER[1] + 0.0035]  // NE
];

// NEW: simple mobile check
const IS_MOBILE = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;

const map = new mapboxgl.Map({
  container: 'map',
  style: 'mapbox://styles/dripvisual/cmed0h0y600sh01sb6pk40r8o?fresh=true',
  center: MAP_CENTER,
  zoom: IS_MOBILE ? 17.6 : 18.3,                 // ← start a bit farther out on mobile
  pitch: 61.957839591686145,
  bearing: -61.59530112412881,
  antialias: true,
  preserveDrawingBuffer: true,
  maxBounds: MAX_BOUNDS,
  minZoom: IS_MOBILE ? 16.2 : 17.5,               // ← allow more zoom-out on mobile
  maxZoom: 21.5
});

// ───────────────────────────────────────────────────────────────────────────────
// Globals
// ───────────────────────────────────────────────────────────────────────────────

let cameraReset = {
  center: [5.108830543530047, 52.075147667677925],
  zoom: IS_MOBILE ? 17.6 : 18.3,                  // ← Home zoom a bit wider on mobile
  pitch: 61.957839591686145,
  bearing: -61.59530112412881,
};

let proposalLayer = null;
let contextLayer = null;
let parkLayer = null;
let unitsLayer = null;
// ───────────────────────────────────────────────────────────────────────────────
// Materials + Textures
// ───────────────────────────────────────────────────────────────────────────────
const glassMat = new THREE.MeshStandardMaterial({
  color: 0xffffff,
  roughness: 0.02,
  metalness: 0.0,
  transparent: true,
  opacity: 0.2,
  side: THREE.FrontSide,
  depthWrite: false
});

const basic = (color) => new THREE.MeshStandardMaterial({ color, roughness: 0.8, metalness: 0.0 });

const basicWhiteMat = basic(0xffffff);
const basicBrickMat = basic(0xECE3E1);
const basicGreenMat = basic(0xC4D3C0);
const basicStructureMat = basic(0xF4F4F4);
const basicWallsMat = basic(0xF4F4F4);
const basicFramesMat = basic(0xD9D9D9);
const basicTimberMat = basic(0xF7ECE0);
const basicFloorsMat = basic(0xffffff);
const basicDetailsMat = basic(0xffffff);


// park materials:
const basicGreen1 = basic(0xafc2a9);
const basicGreen2 = basic(0xC4D3C0);
const basicGreen3 = basic(0xcfdad1);

const basicSports1 = basic(0xefefef);
const basicSports2 = basic(0xffffff);
const basicSports3 = basic(0xc2c2c2);
const basicSports4 = basic(0xcdcdcd);

const treesToonMaterial = new THREE.ShaderMaterial({
  vertexShader: treesVertexShader,
  fragmentShader: treesFragmentShader,
  uniforms: {
    lightDirection: { value: new THREE.Vector3(1, 1, 1).normalize() },
    color: { value: new THREE.Color(0xafc2a9) },
  },
  side: THREE.DoubleSide,
});
const treesOutlineMaterial = new THREE.ShaderMaterial({
  vertexShader: outlineVertexShader,
  fragmentShader: outlineFragmentShader,
  uniforms: {
    lightDirection: { value: new THREE.Vector3(1, 1, 1).normalize() },
    color: { value: new THREE.Color(0x606f5e) },
    outlineThickness: { value: 0.08 },
  },
  side: THREE.BackSide,
});

const onHoverMaterial = new THREE.MeshStandardMaterial({
  color: 0xf9d235,
  roughness: 0.6,
  metalness: 0.1,
  emissive: 0x333300
});

const textureLoader = new THREE.TextureLoader();
const textures = {
  woodTexture: textureLoader.load('textures/Plywood_vdcjecc_4K_BaseColor_PS.jpg'),
  woodTextureDark: textureLoader.load('textures/Plywood_vdcjecc_4K_BaseColor_PS_darker.jpg'),
  woodCLT: textureLoader.load('textures/CLT_UPDATED.jpg'),
  woodCLTDark: textureLoader.load('textures/CLT_UPDATED_darker.jpg'),
  woodCLTSide: textureLoader.load('textures/CLT_side_seamless_UPDATED.jpg')
};
Object.values(textures).forEach(t => {
  // Keep sRGBEncoding to match your current stack
  t.encoding = THREE.sRGBEncoding;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
});


// scroll to parent prevetions:

document.documentElement.style.overscrollBehavior = 'none';
document.body.style.overscrollBehavior = 'none';



// ───────────────────────────────────────────────────────────────────────────────
// QUICK GEO PROBE  (ALT+Click on the map canvas)
// Logs { lngLat:[lng,lat], height:m, level } and a ready-to-paste CAMERA_MARKERS entry
// ───────────────────────────────────────────────────────────────────────────────
function initGeoProbe() {
  const canvas = map.getCanvas();
  const activeLayers = () =>
    [proposalLayer, unitsLayer, parkLayer, contextLayer].filter(l => l && l.modelLoaded);

  canvas.addEventListener('click', (ev) => {
    if (!ev.altKey) return; // hold ALT while clicking to sample
    const r = canvas.getBoundingClientRect();
    const pt = { x: ev.clientX - r.left, y: ev.clientY - r.top };

    // pick across all loaded layers, take the closest hit
    let best = null;
    for (const L of activeLayers()) {
      const hits = L._raycastAt?.(pt) || [];
      for (const h of hits) {
        if (!best || h.distance < best.distance) best = { ...h, _layer: L };
      }
    }
    if (!best) {
      console.info('[geo-probe] No intersection under cursor.');
      return;
    }

    // world-space point (GLTF units — your setup uses Z as "up" in meters)
    const world = best.point.clone();

    // convert to lng/lat using the layer's transform helper
    const ll = best._layer._worldToLngLat(world);

    // meters above model ground (z-up in your pipeline)
    const heightM = world.z;

    const out = {
      lngLat: [Number(ll.lng.toFixed(7)), Number(ll.lat.toFixed(7))],
      height: Number(heightM.toFixed(2)),
      level: (unitsLayer?.currentLevel ?? proposalLayer?.currentLevel ?? null)
    };

    // pretty console output + a copyable marker stub
    const stub = {
      id: 'cam-new',
      lngLat: out.lngLat,
      height: out.height,
      level: out.level ?? 'all',
      media: { type: 'image', src: '' } // swap to video if needed
    };

    console.log(
      '%c[geo-probe]', 'color:#0a0;font-weight:700',
      out,
      '\nPaste into CAMERA_MARKERS:\n',
      JSON.stringify(stub, null, 2)
    );

    // optional: copy the JSON stub to clipboard for convenience
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(JSON.stringify(stub)).catch(() => { });
    }
  }, { passive: true });
}




map.on('load', () => {
  const canvas = map.getCanvas();

  // prevent touch panning from bubbling to parent
  canvas.style.touchAction = 'none';

  // wheel events: prevent default so the parent never scrolls
  canvas.addEventListener('wheel', (e) => e.preventDefault(), { passive: false });

  // touchmove: same idea on mobile
  canvas.addEventListener('touchmove', (e) => e.preventDefault(), { passive: false });

  // NEW: tell the parent we started touching the model area
  // (uses your existing postToParent() helper)
  canvas.addEventListener('touchstart', () => {
    try { postToParent({ type: 'IFRAME_TOUCH_START' }); } catch { }
  }, { passive: true });
  canvas.addEventListener('pointerdown', () => {
    try { postToParent({ type: 'IFRAME_INTERACT_START' }); } catch { }
  }, { passive: true });
});

window.addEventListener('keydown', (e) => {
  const keys = [' ', 'Spacebar', 'PageDown', 'PageUp', 'ArrowDown', 'ArrowUp'];
  if (!keys.includes(e.key)) return;

  const el = e.target;
  const isEditable =
    (el && (el.isContentEditable ||
      ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)));

  // Don’t block typing inside inputs/textareas/contenteditable
  if (isEditable) return;

  e.preventDefault();
}, { passive: false });



// ───────────────────────────────────────────────────────────────────────────────
// Lighting helpers (Mapbox-aligned)
// ───────────────────────────────────────────────────────────────────────────────
function getMapboxLightDirection(preset, theme) {
  if (preset === 'dawn') return theme === 'monochrome' ? [120, 40] : [120, 50];
  if (preset === 'day') return [180, 70];
  if (preset === 'dusk') return theme === 'monochrome' ? [240, 30] : [240, 80];
  if (preset === 'night') return theme === 'monochrome' ? [270, 20] : [270, 20];
  return [180, 20];
}
function sphericalToVector3(azimuthDeg, altitudeDeg, type) {
  const az = THREE.MathUtils.degToRad(azimuthDeg);
  const al = THREE.MathUtils.degToRad(altitudeDeg);
  const m = type === 'context' ? 350 : 50;
  return new THREE.Vector3(
    Math.cos(al) * Math.sin(az) * m,
    Math.cos(al) * Math.cos(az) * m,
    Math.sin(al) * m
  );
}
function getMapboxLightIntensity(preset) {
  return ({ dawn: 1.7, day: 1.2, dusk: 1.2, night: 1.7 }[preset] ?? 1.2);
}
function getMapboxLightColor(preset, theme) {
  const lightColors = {
    dawn: 'hsl(33, 98%, 77%)',
    day: 'hsl(0, 0%, 100%)',
    dusk: theme === 'monochrome' ? 'hsl(30, 0%, 50%)' : 'hsl(30, 98%, 76%)',
    night: theme === 'monochrome' ? 'hsl(0, 0%, 0%)' : 'hsl(0, 0%, 29%)'
  };
  return lightColors[preset] || 'hsl(0, 0%, 100%)';
}
function createDirectionalLight(preset, theme, type) {
  const [azimuth, altitude] = getMapboxLightDirection(preset, theme);
  const direction = sphericalToVector3(azimuth, altitude, type);
  const color = new THREE.Color(getMapboxLightColor(preset, theme));
  const intensity = getMapboxLightIntensity(preset);

  // light issue debuginng:
  // const direction = new THREE.Vector3(120, 100, 80); // X, Y, Z fixed
  // const color = new THREE.Color(getMapboxLightColor(preset, theme));
  // const intensity = getMapboxLightIntensity(preset);

  const light = new THREE.DirectionalLight(color, intensity);
  light.position.copy(direction);
  light.castShadow = true;
  const isCtx = (type === 'context' || type === 'park');
  light.shadow.mapSize.set(isCtx ? 8192 : 4096, isCtx ? 8192 : 4096);
  light.shadow.bias = -0.0005;
  light.shadow.normalBias = 0.01;
  light.shadow.camera.near = 0.1;
  light.shadow.camera.far = isCtx ? 500 : 100;
  light.shadow.camera.left = isCtx ? -300 : -100;
  light.shadow.camera.right = isCtx ? 150 : 100;
  light.shadow.camera.top = isCtx ? 200 : 100;
  light.shadow.camera.bottom = isCtx ? -1000 : -100;
  return light;
}

// ───────────────────────────────────────────────────────────────────────────────
// GLTF utilities
// ───────────────────────────────────────────────────────────────────────────────
function applyTextureReplacement(mesh) {
  const mapByName = {
    'M_wood_detailed': textures.woodTexture,
    'M_wood_detailed_DARKER': textures.woodTextureDark,
    'M_CLT_Top': textures.woodCLT,
    'M_CLT_Top_darker': textures.woodCLTDark,
    'M_CLT_Side': textures.woodCLTSide
  };
  const replacementTexture = mapByName[mesh.material?.name];
  mesh.material = replacementTexture
    ? new THREE.MeshStandardMaterial({ map: replacementTexture, roughness: 0.7, metalness: 0.0 })
    : new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.8, metalness: 0.0 });
  mesh.defaultmaterial = mesh.material;
}

function standardizeBaseMaterial(mesh) {
  const m = mesh.material;
  if (m) {
    m.depthWrite = true;
    m.depthTest = true;
    m.side = THREE.FrontSide;
    if (m.roughness !== undefined) m.roughness = Math.min(0.8, m.roughness);
    m.normalMap = null;
  }
  mesh.castShadow = true;
  mesh.receiveShadow = true;
}

// Tag a mesh with a numeric "level" if we can infer it
function inferLevelForMesh(mesh, useNameFrom = 'userDataFirst') {
  const fromUserData = String(mesh?.userData?.name ?? '');
  const fromName = String(mesh?.name ?? '');
  const src = useNameFrom === 'userDataFirst'
    ? (fromUserData || fromName)
    : (fromName || fromUserData);

  const match = src.match(/\d+/);
  const level = match ? parseInt(match[0], 10) : null;
  mesh.level = Number.isFinite(level) ? level : null;
}

// ───────────────────────────────────────────────────────────────────────────────
// Units data + color helpers
// ───────────────────────────────────────────────────────────────────────────────
const UNIT_TYPE_COLORS = {
  'horeca': 0xF2BF7D,
  'office': 0x3A504A,
  'living lab': 0xBD5F4A,
  'maakateliers': 0xC9A0BC
};

function colorFromUnitType(typeStr) {
  const key = String(typeStr || '').trim().toLowerCase();
  return UNIT_TYPE_COLORS[key] ?? 0xCCCCCC;  // fallback gray
}

// Extract "20" from "Units_Level_5_Unit20"
function parseUnitNumberFromName(name) {
  const s = String(name || '');
  const afterFirst = s.replace(/^[\s\S]*?Unit/i, '');   // everything after 1st "Unit"
  const afterSecond = afterFirst.replace(/^[\s\S]*?Unit/i, ''); // if a 2nd "Unit" exists
  const tail = afterSecond || afterFirst;               // use 2nd pass if it happened
  const m = /^[A-Za-z0-9._-]+/.exec(tail);
  return m ? m[0] : null;
}

function normalizeUnitNumber(val) {
  if (val == null) return null;
  const s = String(val).trim().replace(/\s+/g, '');
  return s.toUpperCase();
}

// Load entries from (1) provided array, (2) window.__UNIT_ENTRIES__, or (3) fetch URL
async function loadUnitEntries({ unitEntries, unitDataUrl } = {}) {
  try {
    let rows = Array.isArray(unitEntries) ? unitEntries
      : Array.isArray(window.__UNIT_ENTRIES__) ? window.__UNIT_ENTRIES__
        : null;

    if (!rows && unitDataUrl) {
      const res = await fetch(unitDataUrl, { cache: 'no-store' });
      if (res.ok) rows = await res.json();
    }

    rows = Array.isArray(rows) ? rows : [];

    // Build a Map keyed by normalized "number"
    const byNumber = new Map();
    for (const r of rows) {
      const k = normalizeUnitNumber(r?.number);
      if (k) byNumber.set(k, r);
    }
    return byNumber;
  } catch (e) {
    console.warn('Failed to load unit entries:', e);
    return new Map();
  }
}

// --- Helpers: localized names & availability labels (place at module scope)
function localizedName(row, lang) {
  const nl = String(row?.name_NL || '').trim();
  const en = String(row?.name || '').trim();
  return (lang === 'nl' && nl) ? nl : (en || `Unit ${row?.number ?? ''}`);
}
function normAvailabilityCode(v) {
  const s = String(v || '').toLowerCase().replace(/\s+/g, '');
  if (s === 'verhuurd' || s === 'leased') return 'leased';
  if (s === 'gereserveerd' || s === 'reserved') return 'reserved';
  if (s === 'beschikbaar' || s === 'available') return 'available';
  if (s === 'sold') return 'sold';
  return s || '';
}
function availabilityLabel(av, lang, t) {
  const code = normAvailabilityCode(av);
  const dict = t?.[lang]?.filterStatus || {};
  return dict[code] || (av || '—');
}

function formatPrice(price, priceMode, lang) {
  if (price == null || price === '') return '—';
  const str = new Intl.NumberFormat('nl-NL', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0
  }).format(Number(price));
  const suffix = window.t?.[lang]?.priceSuffix?.[priceMode] || '';
  return suffix ? `${str} ${suffix}` : str;
}

// --- Live language refresh for cards, headers, and unit-details (module scope)
function refreshLanguageText() {
  const lang = window.currentLang || 'nl';
  const tdict = window.t || {};
  const levelLabel = (tdict[lang]?.details?.level) || (lang === 'nl' ? 'niveau' : 'level');

  // Level headers
  document.querySelectorAll('#filter-results .level-header').forEach(h => {
    const lvl = h.dataset.level;
    h.textContent = `${levelLabel} ${lvl === undefined || lvl === '-999' ? '—' : lvl}`;
  });

  // Cards
  const map = unitsLayer?.unitDataMap;
  if (map) {
    document.querySelectorAll('#filter-results .unit-card').forEach(card => {
      const unit = card.dataset.unit;
      if (!unit) return;
      const normalized = (String(unit).replace(/\D+/g, '') || null);
      const row = map.get(normalized) || map.get(String(unit)) || null;
      if (!row) return;

      const name = localizedName(row, lang);
      const availText = availabilityLabel(row.availability, lang, tdict);
      const availClass = normAvailabilityCode(row.availability);

      const nameEl = card.querySelector('.uc-title .uc-name');
      const availEl = card.querySelector('.availability');
      if (nameEl) nameEl.textContent = name;
      if (availEl) {
        availEl.textContent = availText;
        availEl.className = `availability ${availClass}`;
      }
      const img = card.querySelector('.uc-thumb img');
      if (img) img.alt = `${name} overview`;
    });
  }

  // Unit details (if open)
  const details = document.querySelector('#unit-details');
  if (details && details.style.display !== 'none') {
    const selected = (unitsLayer && unitsLayer._selected && unitsLayer._selected.values().next().value) || null;
    const data = selected?.unitData;
    if (data) {
      unitsLayer._renderUnitDetails?.(data, selected.unitNumber, selected.level);
    } else {
      const unitNumber = details.dataset.unit;
      if (unitNumber && map) {
        const normalized = (String(unitNumber).replace(/\D+/g, '') || null);
        const row = map.get(normalized) || map.get(String(unitNumber)) || null;
        if (row) unitsLayer._renderUnitDetails?.(row, unitNumber, row.level);
      }
    }
  }
}




// device power check:
function isLowPowerDevice(gl) {
  const uaMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const saveData = navigator.connection?.saveData === true;
  const memGB = navigator.deviceMemory || 0;          // 0 => unknown
  const cores = navigator.hardwareConcurrency || 0;   // 0 => unknown
  const dpr = window.devicePixelRatio || 1;

  // Try to read the GPU name (best signal)
  let gpu = '';
  try {
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    if (dbg) gpu = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || '';
  } catch { }

  // “Lower” mobile GPUs + iGPUs
  const weakGPU = /(mali|adreno\s?[12]\d{2}|powervr|u?hd\s?graphics|intel\b|apple\sa\d)/i.test(gpu);

  // Heuristics: any of these → low power
  const lowRam = memGB && memGB <= 4;
  const lowCores = cores && cores <= 4;
  const mobileHiDPR = uaMobile && dpr >= 3;

  return saveData || weakGPU || lowRam || lowCores || mobileHiDPR;
}




// ───────────────────────────────────────────────────────────────────────────────
// Custom layer factory (proposal/context)
// ───────────────────────────────────────────────────────────────────────────────
function createGLTFLayer(modelUrl, type, modelOrigin, options = {}) {
  const {
    interaction = 'none',            // 'hover' | 'click' | 'both' | 'none' | 'unitsHover'
    pickFilter = (o) => o.isMesh && !o.name?.includes('Glass'),
    highlightMaterial = onHoverMaterial,
    onHover = null,
    onClick = null,
    layerKind = type,
    retainHighlightOnSelect = true,
    multiSelect = true,
    //  - 'lte'  => show levels <= selected (proposal)
    //  - 'eq'   => show levels === selected (units)
    levelFilterMode = (type === 'units' ? 'eq' : 'lte'),
    hoverStyle = (type === 'units' ? 'opacity' : 'material'), // 'opacity' | 'material'
    baseOpacity = 0.45,
    hoverOpacity = 0.85,
    selectedOpacity = 1.0,
    detailsContainerSelector = '#details-display',
    unitEntries = null,           // optional: array of rows
    unitDataUrl = 'https://publictwin.info/UtrechtDEPOT/php_scripts/readUnitsData.php'    // optional: fetch fallback
  } = options;

  const modelAltitude = 0;
  const modelRotation = [0, 0, 0];
  const mc = mapboxgl.MercatorCoordinate.fromLngLat(modelOrigin, modelAltitude);
  const transform = {
    translateX: mc.x,
    translateY: mc.y,
    translateZ: mc.z,
    rotateX: modelRotation[0],
    rotateY: modelRotation[1],
    rotateZ: modelRotation[2],
    scale: mc.meterInMercatorCoordinateUnits()
  };

  return {
    id: `${type}-model`,
    type: 'custom',
    renderingMode: '3d',

    // internals
    map: null,
    gl: null,
    scene: null,
    camera: null,
    renderer: null,
    model: null,
    modelGroup: null,
    modelLoaded: false,

    // picking
    raycaster: new THREE.Raycaster(),
    pickables: [],
    _hovered: null,
    _selected: new Set(),

    // level filtering (proposal only)
    levelMeshes: [],
    currentLevel: null,   // null = show all
    minLevel: null,
    maxLevel: null,

    tooltipEl: null,

    onAdd(map, gl) {

      this.map = map;
      this.gl = gl;
      const unitDataPromise = (type === 'units')
        ? loadUnitEntries({ unitEntries, unitDataUrl })
        : Promise.resolve(null);

      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(28, 1, 0.1, 1e6);

      if (type === 'units') {
        this.tooltipEl = document.createElement('div');
        this.tooltipEl.className = 'unit-tooltip';
        document.body.appendChild(this.tooltipEl);
      }

      // click interaction:
      if (type === 'units') {
        this._onClick = (event) => {
          const canvas = map.getCanvas();
          const r = canvas.getBoundingClientRect();
          const hits = this._raycastAt({ x: event.clientX - r.left, y: event.clientY - r.top });
          if (!hits.length) return;
          try { postToParent({ type: 'IFRAME_UNIT_SELECTED' }); } catch { }
          const top = hits[0].object;

          // Single-select by default for clarity
          if (!multiSelect) {
            for (const obj of this._selected) {
              obj.selected = false;
              obj.material.opacity = (obj._baseOpacity ?? baseOpacity);
              this._selected.delete(obj);
            }
          }

          if (this._selected.has(top)) {
            // Toggle off
            this._selected.delete(top);
            top.selected = false;
            top.material.opacity = top.hover && hoverStyle === 'opacity'
              ? (top._hoverOpacity ?? hoverOpacity)
              : (top._baseOpacity ?? baseOpacity);
            this._renderUnitDetails(null); // clear panel
            document.dispatchEvent(new CustomEvent('unit-select', { detail: { unitNumber: null } }));
          } else {
            // Select this one
            this._selected.add(top);
            top.selected = true;
            console.log(top);
            if (hoverStyle === 'opacity') {
              top.material.opacity = (top._selectedOpacity ?? selectedOpacity);
            } else {
              top.material = highlightMaterial; // not used for units, but kept for completeness
            }
            // Show details
            this._renderUnitDetails(top.unitData, top.unitNumber, top.level);
            document.dispatchEvent(new CustomEvent('unit-select', { detail: { unitNumber: top.unitNumber } }));

            const ll = this._meshCenterLngLat(top);
            if (ll) {
              const current = this.map.getZoom();
              const targetZoom = (window.matchMedia && window.matchMedia('(max-width: 768px)').matches)
                ? 18.6   // ← lighter zoom-in on mobile
                : 19.5;

              this.map.easeTo({
                center: ll,
                zoom: targetZoom,
                duration: 2000,
                easing: t => 1 - Math.pow(1 - t, 3),
                essential: true
              });
            }
          }
        };
        map.getCanvas().addEventListener('click', this._onClick, { passive: true });
      }

      // lighting
      const theme = map.getConfigProperty('basemap', 'theme') || 'default';
      const preset = map.getConfigProperty('basemap', 'lightPreset') || 'day';
      const dirLight = createDirectionalLight(preset, theme, type);

      // dirLight.target.position.set(50,50,2);
      // this.scene.add(dirLight.target);

      this.scene.add(dirLight);
      this.scene.add(new THREE.AmbientLight(0xffffff, 1.2));

      // ground receiver for soft contact shadows
      const shadowPlane = new THREE.Mesh(
        new THREE.PlaneGeometry(15000, 15000),
        new THREE.ShadowMaterial({ opacity: 0.3 })
      );
      shadowPlane.rotation.x = 0;
      shadowPlane.position.y = 0;
      shadowPlane.receiveShadow = true;
      this.scene.add(shadowPlane);

      // group to hold model
      this.modelGroup = new THREE.Group();
      this.scene.add(this.modelGroup);

      // renderer shares Mapbox canvas context
      this.renderer = new THREE.WebGLRenderer({ canvas: map.getCanvas(), context: gl, antialias: true });
      this.renderer.autoClear = false;

      const lowPower = isLowPowerDevice(gl);
      if (!lowPower) {
        this.renderer.shadowMap.enabled = true;
        // Prefer soft shadows if supported; otherwise cheaper Basic
        const supportsPCFSoft = !!gl.getExtension('OES_standard_derivatives');
        this.renderer.shadowMap.type = supportsPCFSoft ? THREE.PCFSoftShadowMap : THREE.BasicShadowMap;
      } else {
        this.renderer.shadowMap.enabled = false;
      }

      // load GLTF
      const loader = new GLTFLoader();
      loader.load(
        modelUrl,
        (gltf) => {
          const model = gltf.scene;
          this.model = model;

          const finishAdd = () => {
            this.modelGroup.add(model);
            this.modelLoaded = true;
            if (type === 'proposal' || type === 'units') this._applyLevelFilter();
          };

          if (type === 'units') {
            // Wait for unit data, then traverse & assign materials
            unitDataPromise.then((byNumber) => {
              this.unitDataMap = byNumber;
              model.traverse((child) => {
                if (!child.isMesh) return;

                // Standard flags + bookkeeping
                standardizeBaseMaterial(child);
                child.hover = false;
                child.selected = false;
                child.filtered = false;

                // Tag levels
                inferLevelForMesh(child, 'userDataFirst');
                if (child.level !== null) {
                  this.levelMeshes.push(child);
                  this.minLevel = this.minLevel === null ? child.level : Math.min(this.minLevel, child.level);
                  this.maxLevel = this.maxLevel === null ? child.level : Math.max(this.maxLevel, child.level);
                  this.maxLevel = (this.maxLevel == null) ? 6 : Math.max(this.maxLevel, 6);
                }

                // Link to DB row by "number" from "Units_Level_X_UnitNN"
                const unitNumber = parseUnitNumberFromName(child.userData?.name || child.name);
                // console.log("unitNumber", unitNumber)
                const entry = unitNumber ? byNumber?.get(normalizeUnitNumber(unitNumber)) : null;
                child.unitNumber = unitNumber;
                child.unitData = entry || null;

                // Normalize availability
                const normAvail = (v) => {
                  const s = String(v || '').toLowerCase().replace(/\s+/g, '');
                  if (s === 'verhuurd' || s === 'leased') return 'leased';
                  if (s === 'gereserveerd' || s === 'reserved') return 'reserved';
                  if (s === 'beschikbaar' || s === 'available') return 'available';
                  if (s === 'sold') return 'sold';
                  return s;
                };
                const avNorm = normAvail(entry?.availability);

                // Keep model color overrides
                const typeColor = colorFromUnitType(entry?.type);
                const colorHex =
                  avNorm === 'leased' ? 0x3c3c3c :
                    avNorm === 'reserved' ? 0x8d8d8d :
                      typeColor;

                const mat = new THREE.MeshStandardMaterial({
                  color: colorHex,
                  roughness: 0.8,
                  metalness: 0.0,
                  transparent: true,
                  opacity: baseOpacity
                });
                child.material = mat;
                child.defaultmaterial = mat;

                child.userData._avNorm = avNorm;

                child.castShadow = false;
                child.receiveShadow = false;

                // ✅ Allow leased in pickables (clickable)
                if (pickFilter(child)) {
                  this.pickables.push(child);
                }

              });

              finishAdd();
              if (type === 'proposal') {
                // compute once after the model is added
                this._bboxAnchorLngLat = this._computeProposalAnchorLngLat?.() || null;
              }
            });
            return; // important: don't fall through; we'll add once data is applied
          }

          // ── proposal/context (unchanged except your own materials logic) ───────────
          model.traverse((child) => {
            if (!child.isMesh) return;

            standardizeBaseMaterial(child);
            child.defaultmaterial = child.material;
            child.hover = false;
            child.selected = false;
            child.filtered = false;

            if (type === 'proposal') {
              // your existing semantic materials...
              const srcName = (child.userData?.name || child.name || '');
              const name = child.name || '';
              if (name.includes('Glass')) { child.castShadow = child.receiveShadow = false; child.material = glassMat; child.defaultmaterial = child.material; }
              else if (name.includes('Green')) { child.material = basicGreenMat; child.defaultmaterial = child.material; }
              else if (name.includes('Brick')) { child.material = basicBrickMat; child.defaultmaterial = child.material; }
              else if (name.includes('Frame')) { child.material = basicFramesMat; child.defaultmaterial = child.material; }
              else if (name.includes('Timber')) { child.material = basicTimberMat; child.defaultmaterial = child.material; }
              else if (name.includes('Structure')) { child.material = basicStructureMat; child.defaultmaterial = child.material; }
              else if (name.includes('Walls')) { child.material = basicWallsMat; child.defaultmaterial = child.material; }
              else if (name.includes('Floors')) { child.material = basicFloorsMat; child.defaultmaterial = child.material; }
              else if (name.includes('Details')) { child.material = basicDetailsMat; child.defaultmaterial = child.material; }
              else { applyTextureReplacement(child); }

              // ADD: edges for "brick" or "timber"
              if (/brick|timber/i.test(srcName) && !child.userData._edgesHelper) {
                const edgeGeom = new THREE.EdgesGeometry(child.geometry);
                const edgeMat = new THREE.LineBasicMaterial({
                  color: 0x6d6d6d,
                  depthTest: true,
                  depthWrite: false,
                  transparent: true,
                  opacity: 0.9,
                });
                const edges = new THREE.LineSegments(edgeGeom, edgeMat);
                edges.renderOrder = (child.renderOrder ?? 0) + 1; // draw after the mesh
                edges.frustumCulled = child.frustumCulled;
                child.add(edges);
                child.userData._edgesHelper = edges;
              }


              inferLevelForMesh(child, 'userDataFirst');
              if (child.level !== null) {
                this.levelMeshes.push(child);
                this.minLevel = this.minLevel === null ? child.level : Math.min(this.minLevel, child.level);
                this.maxLevel = this.maxLevel === null ? child.level : Math.max(this.maxLevel, child.level);
              }
            } else if (type === 'context') {
              // context
              applyTextureReplacement(child);

              // ADD EDGES for meshes whose name contains "standard" (case-insensitive)
              const srcName = (child.userData?.name || child.name || '');
              if (/standard|Rebuilt/i.test(srcName)) {
                const edgeGeom = new THREE.EdgesGeometry(child.geometry /*, thresholdAngleOptional */);
                const edgeMat = new THREE.LineBasicMaterial({
                  color: 0x6d6d6d,
                  linewidth: 0.1,
                  depthTest: true,
                  depthWrite: false,
                  transparent: true,
                  opacity: 0.5,
                });

                const edges = new THREE.LineSegments(edgeGeom, edgeMat);
                edges.renderOrder = (child.renderOrder ?? 0) + 1; // draw after the mesh
                // optional: keep culling behavior in sync
                edges.frustumCulled = child.frustumCulled;

                // Attach to the mesh so it follows transforms & hides with it
                child.add(edges);
                child.userData._edgesHelper = edges; // keep a handle for disposal if needed
              }
              // if (/detailed|frame/i.test(srcName)) {
              //   const edgeGeom = new THREE.EdgesGeometry(child.geometry /*, thresholdAngleOptional */);
              //   const edgeMat  = new THREE.LineBasicMaterial({
              //     color: 0x6d6d6d,
              //     linewidth:0.1,
              //     depthTest: true,
              //     depthWrite: false,
              //     transparent: true,
              //     opacity: 0.5,
              //   });

              //   const edges = new THREE.LineSegments(edgeGeom, edgeMat);
              //   edges.renderOrder = (child.renderOrder ?? 0) + 1; // draw after the mesh
              //   // optional: keep culling behavior in sync
              //   edges.frustumCulled = child.frustumCulled;

              //   // Attach to the mesh so it follows transforms & hides with it
              //   child.add(edges);
              //   child.userData._edgesHelper = edges; // keep a handle for disposal if needed
              // }



            } else {

              const name = child.name || '';
              if (name.includes('Green1')) { child.material = basicGreen1; child.defaultmaterial = child.material; }
              else if (name.includes('Green2')) { child.material = basicGreen2; child.defaultmaterial = child.material; }
              else if (name.includes('Green3')) { child.material = basicGreen3; child.defaultmaterial = child.material; }
              else if (name.includes('Sports1')) { child.material = basicSports1; child.defaultmaterial = child.material; }
              else if (name.includes('Sports2')) { child.material = basicSports2; child.defaultmaterial = child.material; }
              else if (name.includes('Sports3')) { child.material = basicSports3; child.defaultmaterial = child.material; }
              else if (name.includes('Sports4')) { child.material = basicSports4; child.defaultmaterial = child.material; }
              else if (name.includes('Crown')) { child.material = basicGreen2; child.defaultmaterial = child.material; }
              else { child.material = basicWhiteMat; child.defaultmaterial = child.material; }





            }

            if (pickFilter(child)) this.pickables.push(child);
          });

          finishAdd();
        },
        undefined,
        (err) => console.error('GLTF Load Error:', err)
      );

      // Event listeners based on interaction mode
      const canvas = map.getCanvas();

      if (interaction === 'hover' || interaction === 'both') {
        this._onMouseMove = (event) => {
          const r = canvas.getBoundingClientRect();
          this._hoverRaycast({ x: event.clientX - r.left, y: event.clientY - r.top });
        };
        canvas.addEventListener('mousemove', this._onMouseMove, { passive: true });
      }

      if (interaction === 'click' || interaction === 'both') {
        this._onClick = (event) => {
          const r = canvas.getBoundingClientRect();
          const hits = this._raycastAt({ x: event.clientX - r.left, y: event.clientY - r.top });
          if (!hits.length) return;

          const top = hits[0].object;


          // Single-select by default for clarity
          if (!multiSelect) {
            for (const obj of this._selected) {
              obj.selected = false;
              obj.material.opacity = (obj._baseOpacity ?? baseOpacity);
              this._selected.delete(obj);
            }
          }

          if (this._selected.has(top)) {
            // toggle off
            this._selected.delete(top);
            top.selected = false;
            top.material = top.hover && retainHighlightOnSelect ? highlightMaterial :
              (top.filtered ? onHoverMaterial : top.defaultmaterial);
          } else {
            this._selected.add(top);
            top.selected = true;
            if (retainHighlightOnSelect) top.material = highlightMaterial;
          }
        };
        canvas.addEventListener('click', this._onClick, { passive: true });
      }
      if (interaction === 'unitsHover') {
        this._onMouseMove = (event) => {
          const r = canvas.getBoundingClientRect();
          this._hoverRaycast({ x: event.clientX - r.left, y: event.clientY - r.top });
          this._updateUnitTooltip(event);
        };
        canvas.addEventListener('mousemove', this._onMouseMove, { passive: true });

        // Hide tooltip whenever the pointer leaves the canvas…
        this._onCanvasLeave = () => this._hideUnitTooltip();
        canvas.addEventListener('mouseleave', this._onCanvasLeave, { passive: true });

        // …or whenever the pointer is over anything that is not the canvas (UI overlays, panels, etc.)
        this._onPointerOverGlobal = (ev) => {
          if (ev.target !== canvas) this._hideUnitTooltip();
        };
        document.addEventListener('pointerover', this._onPointerOverGlobal, true);

      }
    },

    onRemove() {
      const canvas = this.map?.getCanvas?.();
      if (canvas) {
        if (this._onMouseMove) canvas.removeEventListener('mousemove', this._onMouseMove);
        if (this._onClick) canvas.removeEventListener('click', this._onClick);
      }
      if (this.renderer) {
        this.renderer.forceContextLoss?.();
        this.renderer.dispose();
      }
      if (this.tooltipEl?.parentNode) {
        this.tooltipEl.parentNode.removeChild(this.tooltipEl); // +++ remove tooltip node
      }
      if (this._onCanvasLeave) canvas.removeEventListener('mouseleave', this._onCanvasLeave);
      if (this._onPointerOverGlobal) document.removeEventListener('pointerover', this._onPointerOverGlobal, true);

    },

    render(gl, matrix) {
      // update camera
      const canvas = this.map.getCanvas();
      this.camera.aspect = canvas.width / canvas.height;
      this.camera.updateProjectionMatrix();

      const rotX = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(1, 0, 0), transform.rotateX);
      const rotY = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 1, 0), transform.rotateY);
      const rotZ = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 0, 1), transform.rotateZ);

      const m = new THREE.Matrix4().fromArray(matrix);
      const l = new THREE.Matrix4()
        .makeTranslation(transform.translateX, transform.translateY, transform.translateZ)
        .scale(new THREE.Vector3(transform.scale, -transform.scale, transform.scale))
        .multiply(rotX)
        .multiply(rotY)
        .multiply(rotZ);

      this.camera.projectionMatrix = m.multiply(l);
      this.camera.projectionMatrixInverse.copy(this.camera.projectionMatrix).invert();

      this.renderer.setSize(canvas.width, canvas.height, false);
      this.renderer.resetState();
      this.renderer.render(this.scene, this.camera);
      this.map.triggerRepaint();
    },

    // ── Level filter API (proposal only) ───────────────────────────────────────
    setLevel(level) {
      if (this.currentLevel === level) return;
      this.currentLevel = level; // number or null
      this._applyLevelFilter();
    },
    showAllLevels() {
      this.setLevel(null);
    },
    stepLevel(delta) {
      if (this.minLevel === null || this.maxLevel === null) return; // not ready
      let next = this.currentLevel;
      if (next === null) next = delta > 0 ? this.minLevel : this.maxLevel;
      else next = Math.min(this.maxLevel, Math.max(this.minLevel, next + delta));
      this.setLevel(next);
    },
    _applyLevelFilter() {
      if ((type !== 'proposal' && type !== 'units') || !this.modelLoaded) return;

      const noLevelSelected = (this.currentLevel === null);

      // ── Units: exact-match, and hide ALL when no level selected ────────────────
      if (type === 'units') {
        if (noLevelSelected) {
          // Hide every mesh (tagged or not) whenever no level is selected
          this.model.traverse(obj => { if (obj.isMesh) obj.visible = false; });
        } else {
          // Show only meshes with level === currentLevel
          for (const mesh of this.levelMeshes) {
            mesh.visible = (mesh.level === this.currentLevel);
          }
          // Non-tagged meshes are hidden for units
          this.model.traverse(obj => {
            if (!obj.isMesh) return;
            if (obj.level === undefined || obj.level === null) obj.visible = false;
          });
        }
        this.map?.triggerRepaint?.();
        return;
      }

      // ── Proposal: original behavior (≤ selected; “show all” when none) ─────────
      const showAll = noLevelSelected;
      for (const mesh of this.levelMeshes) {
        mesh.visible = showAll ? true : (mesh.level <= this.currentLevel);
      }
      // Non-tagged visible only when “show all”
      this.model.traverse(obj => {
        if (!obj.isMesh) return;
        if (obj.level === undefined || obj.level === null) obj.visible = showAll;
      });

      this.map?.triggerRepaint?.();
    },
    // ── Raycasting helpers ────────────────────────────────────────────────────
    _screenToRay(point) {
      const ndc = {
        x: (point.x / this.map.transform.width) * 2 - 1,
        y: 1 - (point.y / this.map.transform.height) * 2
      };
      const invProj = this.camera.projectionMatrix.clone().invert();
      const camPos = new THREE.Vector3().applyMatrix4(invProj);
      const mousePos = new THREE.Vector3(ndc.x, ndc.y, 1).applyMatrix4(invProj);
      const dir = mousePos.sub(camPos).normalize();
      return { camPos, dir };
    },
    _raycastAt(point) {
      if (!this.modelLoaded) return [];
      const { camPos, dir } = this._screenToRay(point);
      this.raycaster.set(camPos, dir);

      // Raycast all pickables, then keep only those that are actually visible in the scene graph.
      const allHits = this.raycaster.intersectObjects(this.pickables, true);
      const visibleHits = allHits.filter(h => this._isVisibleInTree(h.object));

      return visibleHits;
    },
    _hoverRaycast(point) {
      if (!this.modelLoaded) return;
      const hits = this._raycastAt(point);
      const firstVisible = hits[0] || null;
      const hovered = firstVisible ? firstVisible.object : null;

      if (typeof onHover === 'function') onHover(firstVisible, this);
      if (hovered === this._hovered) return;

      // clear previous hovered
      if (this._hovered && (!this._hovered.selected || !retainHighlightOnSelect)) {
        this._hovered.hover = false;
        if (type === 'units' && hoverStyle === 'opacity') {
          this._hovered.material.opacity = (this._hovered._baseOpacity ?? baseOpacity);
        } else {
          this._hovered.material = this._hovered.filtered ? onHoverMaterial : this._hovered.defaultmaterial;
        }
      }

      // set new hovered
      this._hovered = hovered || null;
      if (this._hovered) {
        this._hovered.hover = true;
        if (type === 'units' && hoverStyle === 'opacity') {
          const target = this._hovered.selected
            ? (this._hovered._selectedOpacity ?? selectedOpacity)
            : (this._hovered._hoverOpacity ?? hoverOpacity);
          this._hovered.material.opacity = target;
        } else {
          this._hovered.material = highlightMaterial;
        }
      }

      // ensure tooltip hides if nothing hovered
      if (!this._hovered && this.tooltipEl) this._hideUnitTooltip();


    },
    _isVisibleInTree(obj) {
      let cur = obj;
      while (cur) {
        if (cur.visible === false) return false;
        cur = cur.parent;
      }
      return true;
    },
    _updateUnitTooltip(evt) {
      if (type !== 'units' || !this.tooltipEl) return;

      const hovered = this._hovered;
      if (!hovered || !hovered.visible || !hovered.unitData) {
        this._hideUnitTooltip();
        return;
      }

      const lang = window.currentLang || 'nl';
      const d = hovered.unitData;

      // name (localized)
      const nameText = (typeof localizedName === 'function')
        ? localizedName(d, lang)
        : (d.name || `Unit ${hovered.unitNumber ?? ''}`);

      // price (locale-aware)
      const hasPrice = d.price != null && d.price !== '';
      const locale = lang === 'en' ? 'en-US' : 'nl-NL';
      const priceText = hasPrice
        ? new Intl.NumberFormat(locale, {
          style: 'currency', currency: 'EUR', maximumFractionDigits: 0
        }).format(Number(d.price))
        : '—';

      // availability (localized label + normalized class)
      const availClass = (typeof normAvailabilityCode === 'function')
        ? normAvailabilityCode(d.availability)
        : String(d.availability || '').toLowerCase().replace(/\s+/g, '');
      const availLabel = (typeof availabilityLabel === 'function')
        ? availabilityLabel(d.availability, lang, window.t)
        : (String(d.availability || '').trim() || '—');

      this.tooltipEl.innerHTML = `
    <span class="name">${nameText}</span>
    <span class="price">${priceText}</span>
    <span class="availability ${availClass}">${availLabel}</span>
  `;

      // position (unchanged)
      this.tooltipEl.style.left = `${evt.clientX}px`;
      this.tooltipEl.style.top = `${evt.clientY}px`;
      this.tooltipEl.classList.add('is-visible');
    },

    refreshTooltipLanguage() {
      if (type !== 'units' || !this.tooltipEl) return;
      const hovered = this._hovered;
      if (!hovered || !hovered.visible || !hovered.unitData) return;

      const lang = window.currentLang || 'nl';
      const d = hovered.unitData;
      const nameText = localizedName ? localizedName(d, lang) : (d.name || `Unit ${hovered.unitNumber ?? ''}`);

      const priceText = formatPrice(d.price, d.price_mode, lang);

      const availClass = normAvailabilityCode ? normAvailabilityCode(d.availability)
        : String(d.availability || '').toLowerCase().replace(/\s+/g, '');
      const availLabel = availabilityLabel ? availabilityLabel(d.availability, lang, window.t)
        : (String(d.availability || '').trim() || '—');

      this.tooltipEl.innerHTML = `
        <span class="name">${nameText}</span>
        <span class="price">${priceText}</span>
        <span class="availability ${availClass}">${availLabel}</span>
      `;
    },
    _hideUnitTooltip() {
      if (this.tooltipEl) this.tooltipEl.classList.remove('is-visible');
    },
    _renderUnitDetails(data, unitNumber, meshLevel) {
      const container = document.querySelector(detailsContainerSelector);
      if (!container) return;

      let details = container.querySelector('#unit-details');
      const logo = container.querySelector('#depot-logo');
      if (!details) {
        details = document.createElement('div');
        details.id = 'unit-details';
        details.className = 'unit-details chamfer-all';
        if (logo && logo.parentNode) logo.parentNode.insertBefore(details, logo.nextSibling);
        else container.appendChild(details);
      }

      if (!data) {
        details.innerHTML = '';
        details.style.display = 'none';
        details.removeAttribute('data-unit'); // clear remembered unit
        return;
      }

      const lang = window.currentLang || 'nl';
      const labels = (window.t?.[lang]?.details) || {
        title: 'geselecteerde unit:',
        name: 'naam', type: 'type', level: 'niveau', availability: 'beschikbaarheid', price: 'prijs', area: 'oppervlakte'
      };

      const nameText = localizedName(data, lang);
      const level = (data.level != null ? data.level : (meshLevel ?? '—'));
      const price = formatPrice(data.price, data.price_mode, lang);
      const area = (data.area != null && data.area !== '') ? `${data.area} m²` : '—';
      const imgUrl = data.overviewimage || data.overviewImage || null;

      const availRaw = String(data.availability ?? '').trim();
      const availClass = normAvailabilityCode(availRaw); // class stays normalized
      const availText = availabilityLabel(availRaw, lang, window.t);
      // ...inside _renderUnitDetails(data, unitNumber, meshLevel)
      const interestBtnLabel = (window.t?.[lang]?.interest) || (lang === 'nl' ? 'Interesse tonen' : "I'm interested");

      details.innerHTML = `
  <div class="ud-title">${labels.title}</div>
  <div class="ud-media">
    ${imgUrl ? `<img src="${imgUrl}" alt="${nameText} overview">` : ''}
  </div>
  <div class="ud-row">
    <span class="ud-label">${labels.name}: </span>
    <span class="uc-value">${nameText}</span>
    <span class="uc-value">${unitNumber}${data.level != null ? `.L${data.level}` : ''}</span>
  </div>
  <div class="ud-row">
    <span class="ud-label">${labels.type}: </span>
    <span class="ud-value">${data.type || '—'}</span>
  </div>
  <div class="ud-row">
    <span class="ud-label">${labels.level}: </span>
    <span class="ud-value">${level}</span>
  </div>
  <div class="ud-row">
    <span class="ud-label">${labels.availability}: </span>
    <span class="ud-value"><span class="availability ${availClass}">${availText}</span></span>
  </div>
  <div class="ud-row">
    <span class="ud-label">${labels.price}: </span>
    <span class="ud-value">${price}</span>
  </div>
  <div class="ud-row">
    <span class="ud-label">${labels.area}:</span>
    <span class="ud-value">${area}</span>
  </div>

  <!-- NEW: unit-specific Interested button -->
  <div class="ud-actions">
    <button type="button" class="interested-unit-btn" data-role="interested-unit">
      ${interestBtnLabel}
    </button>
  </div>
`;
      details.style.display = 'block';

      // remember which unit is shown so we can live-refresh on language toggle
      details.dataset.unit = String(unitNumber ?? '');
    },


    _clearUnitDetails() {
      const container = document.querySelector(detailsContainerSelector);
      const details = container?.querySelector?.('#unit-details');
      if (details) { details.innerHTML = ''; details.style.display = 'none'; }
    },
    // +++ UI filter state from the panel (type/status/price)
    _uiFilter: null,

    setUIFilters({ typeSet, statusSet, areaMin, areaMax }) {
      this._uiFilter = { typeSet, statusSet, areaMin, areaMax };
      this._updateVisibilityByFilters();
    },

    _testUnitFilter(data) {
      if (!data) return false;
      const f = this._uiFilter;
      if (!f) return true; // no filters -> pass

      // TYPE (OR) – pass if none selected or data.type in the set
      const typeVal = String(data.type || '').trim().toLowerCase();
      const passType = !f.typeSet || f.typeSet.size === 0 || f.typeSet.has(typeVal);

      // STATUS (OR)
      const avNorm = (v => {
        const s = String(v || '').toLowerCase().replace(/\s+/g, '');
        if (s === 'verhuurd' || s === 'leased') return 'leased';
        if (s === 'gereserveerd' || s === 'reserved') return 'reserved';
        if (s === 'beschikbaar' || s === 'available') return 'available';
        if (s === 'sold') return 'sold';
        return s;
      })(data.availability);
      const passStatus = !f.statusSet || f.statusSet.size === 0 || f.statusSet.has(avNorm);

      // AREA (always applied; defaults cover all)
      const a = (typeof data.area === 'number') ? data.area : parseFloat(data.area);
      const passArea = Number.isFinite(a) && a >= f.areaMin && a <= f.areaMax;

      return passType && passStatus && passArea;

      return passType && passStatus && passPrice;
    },

    _updateVisibilityByFilters() {
      // console.log(this);
      // if (this.layerKind !== 'units' || !this.modelLoaded) return;

      // If no level selected: hide everything (UI list can still show all DB rows)
      if (this.currentLevel === null) {
        this.model.traverse(o => { if (o.isMesh) o.visible = false; });
        // Clear selection/details if they just became invisible
        for (const obj of Array.from(this._selected)) {
          obj.selected = false;
          obj.material.opacity = (obj._baseOpacity ?? 0.55);
          this._selected.delete(obj);
        }
        this._renderUnitDetails?.(null);
        this.map?.triggerRepaint?.();
        return;
      }

      // Start with all unit meshes invisible
      for (const mesh of this.levelMeshes) mesh.visible = false;

      // Apply level gate AND UI filters: only enable meshes on the selected level that pass
      for (const mesh of this.levelMeshes) {
        if (mesh.level !== this.currentLevel) continue;
        const pass = this._testUnitFilter(mesh.unitData);
        mesh.visible = !!pass;
      }

      // Hide any non-tagged meshes (safety)
      this.model.traverse(o => {
        if (!o.isMesh) return;
        if (o.level === undefined || o.level === null) o.visible = false;
      });

      // Deselect anything that’s not visible anymore and clear panel if needed
      let cleared = false;
      for (const obj of Array.from(this._selected)) {
        if (!obj.visible) {
          obj.selected = false;
          obj.material.opacity = (obj._baseOpacity ?? 0.55);
          this._selected.delete(obj);
          cleared = true;
        }
      }
      if (cleared) this._renderUnitDetails?.(null);

      this.map?.triggerRepaint?.();
    },
    _meshCenterLngLat(mesh) {
      if (!mesh?.geometry) return null;

      // ensure we have a sphere
      if (!mesh.geometry.boundingSphere) mesh.geometry.computeBoundingSphere();
      const cLocal = mesh.geometry.boundingSphere.center.clone();

      // mesh local -> world (GLTF scene)
      mesh.updateWorldMatrix(true, false);
      const cWorld = cLocal.applyMatrix4(mesh.matrixWorld);

      // world (GLTF meters) -> Mercator world units (same transform used in `render`)
      const s = transform.scale;                        // meter -> mercator units at this lat
      const x = transform.translateX + cWorld.x * s;
      const y = transform.translateY + cWorld.y * -s;   // note: Y flipped in your `l` matrix
      const z = transform.translateZ + cWorld.z * s;

      // Mercator -> LngLat
      const mc = new mapboxgl.MercatorCoordinate(x, y, z);
      return mc.toLngLat(); // {lng, lat}
    },
    _bboxAnchorLngLat: null,

    _worldToLngLat(worldVec3) {
      const s = transform.scale;
      const x = transform.translateX + worldVec3.x * s;
      const y = transform.translateY + worldVec3.y * -s; // Y flipped
      const z = transform.translateZ + worldVec3.z * s;
      return new mapboxgl.MercatorCoordinate(x, y, z).toLngLat();
    },

    _getWorldBBoxAll() {
      if (!this.modelLoaded || !this.model) return null;
      const box = new THREE.Box3();
      let hasAny = false;

      this.model.traverse((o) => {
        if (!o.isMesh || !o.geometry) return;
        if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
        o.updateWorldMatrix(true, false);
        const bb = o.geometry.boundingBox.clone().applyMatrix4(o.matrixWorld);
        if (!hasAny) { box.copy(bb); hasAny = true; } else { box.union(bb); }
      });

      return hasAny ? box : null;
    },

    _computeProposalAnchorLngLat() {
      const bb = this._getWorldBBoxAll();
      if (!bb) return null;
      const centerWorld = bb.getCenter(new THREE.Vector3());
      return this._worldToLngLat(centerWorld);
    },




  };
}


function setLevelUIActive(level) {
  const levelOptions = Array.from(document.querySelectorAll('#level-select .level-option'));
  const showAllBtn = document.querySelector('#level-show-all .show-all');
  levelOptions.forEach(el => el.classList.remove('active'));
  showAllBtn?.classList.remove('active');

  const match = levelOptions.find(el => String(el.textContent).trim() === String(level));
  match?.classList.add('active');

  // NEW: update the mobile badge next to ▼
  const badge = document.getElementById('level-current-indicator');
  if (badge) {
    // Show number when a level is selected; show "all" when none
    const label = (level === null || level === undefined || level === '' || Number.isNaN(level))
      ? 'all'
      : String(level);
    badge.textContent = label;
  }
}






// ───────────────────────────────────────────────────────────────────────────────
// UI wiring for level controls
// ───────────────────────────────────────────────────────────────────────────────

// Map each floor to the unit-type filter it should auto-select
const LEVEL_TYPE_MAP = {
  0: 'horeca',
  1: 'maakateliers',
  2: 'living lab',
  3: 'living lab',
  4: 'office',
  5: 'office'
};

function selectFilterForLevel(level) {
  const typeChecks = Array.from(
    document.querySelectorAll('#filtering-system .filter-type .type-checkbox')
  );
  // uncheck all type filters first
  typeChecks.forEach(cb => { cb.checked = false; });

  const targetType = LEVEL_TYPE_MAP[level];
  if (targetType) {
    const match = typeChecks.find(cb => {
      const row = cb.closest('.filter-type');
      return row?.dataset?.type === targetType;
    });
    if (match) match.checked = true;
  }

  // trigger filter update
  const evt = new Event('change', { bubbles: true });
  typeChecks[0]?.dispatchEvent(evt);
}

function wireLevelUI() {
  const levelOptions = Array.from(document.querySelectorAll('#level-select .level-option'));
  const showAllBtn = document.querySelector('#level-show-all .show-all');
  const upBtn = document.getElementById('level-switch-up');
  const downBtn = document.getElementById('level-switch-down');
  const indicator = document.getElementById('level-current-indicator');

  // helper: update the badge text
  const updateIndicator = (lvl) => {
    if (!indicator) return;
    if (lvl === null || lvl === undefined || Number.isNaN(lvl)) {
      indicator.textContent = 'all';
    } else {
      indicator.textContent = String(lvl);
    }
  };
  // initialize to 0 (requested)
  updateIndicator(0);

  const setActive = (level) => {
    levelOptions.forEach(el => el.classList.remove('active'));
    showAllBtn?.classList.remove('active');

    if (level === null) {
      showAllBtn?.classList.add('active');
    } else {
      const match = levelOptions.find(el => String(el.textContent).trim() === String(level));
      match?.classList.add('active');
    }
  };

  levelOptions.forEach(el => {
    el.addEventListener('click', () => {
      const val = parseInt(String(el.textContent).trim(), 10);
      if (!Number.isFinite(val)) return;
      proposalLayer?.setLevel?.(val);
      unitsLayer?.setLevel?.(val);     // NEW: drive units too
      setActive(val);
      updateIndicator(val);
      selectFilterForLevel(val);
      zoomReset();
    });
  });

  showAllBtn?.addEventListener('click', () => {
    proposalLayer?.showAllLevels?.();
    unitsLayer?.showAllLevels?.();
    selectFilterForLevel(null);          // clear type filters on "all"
    if (typeof setLevelUIActive === 'function') setLevelUIActive(null);
    setActive(null);
    updateIndicator(null);               // ← show "all"
    zoomReset();
  });

  upBtn?.addEventListener('click', () => {
    proposalLayer?.stepLevel?.(+1);
    unitsLayer?.stepLevel?.(+1);
    const cur = proposalLayer?.currentLevel ?? unitsLayer?.currentLevel ?? null;
    setActive(cur);
    updateIndicator(cur);                // ← update on ▲
    selectFilterForLevel(cur);
    zoomReset();
  });

  downBtn?.addEventListener('click', () => {
    proposalLayer?.stepLevel?.(-1);
    unitsLayer?.stepLevel?.(-1);
    const cur = proposalLayer?.currentLevel ?? unitsLayer?.currentLevel ?? null;
    setActive(cur);
    updateIndicator(cur);                // ← update on ▼
    selectFilterForLevel(cur);
    zoomReset();
  });

  setActive(null);
}



// ───────────────────────────────────────────────────────────────────────────────
// Filtering UI → render matching units as cards
// ───────────────────────────────────────────────────────────────────────────────
function wireFilteringUI() {
  const panel = document.getElementById('filtering-system');
  if (!panel) return;

  // ── refs ────────────────────────────────────────────────────────────────────
  const typeRows = Array.from(panel.querySelectorAll('.filter-type')); // whole colored rows
  const typeChecks = Array.from(panel.querySelectorAll('.filter-type .type-checkbox'));
  const statusChecks = Array.from(panel.querySelectorAll('input.filter-status[type="checkbox"]'));
  const rangeMin = panel.querySelector('#areaMin');
  const rangeMax = panel.querySelector('#areaMax');
  const rangeMinVal = panel.querySelector('#areaMinValue');
  const rangeMaxVal = panel.querySelector('#areaMaxValue');
  const sliderRange = panel.querySelector('#sliderRange');
  const resetWrap = panel.querySelector('#filter-reset');
  const resetBtn = resetWrap?.querySelector('.filter-reset') || resetWrap;
  const showAllBtn = document.querySelector('#level-show-all .show-all');

  // results container
  let results = document.getElementById('filter-results');
  if (!results) {
    results = document.createElement('div');
    results.id = 'filter-results';
    results.className = 'filter-results';
    document.body.appendChild(results);
  }

  // Move #filter-results between body (desktop) and #filtering-system (mobile)
  const mobileMQ = window.matchMedia(
    '(max-width: 768px), (max-width: 1024px) and (orientation: portrait)'
  );
  function placeResults() {
    if (mobileMQ.matches) {
      // Mobile: inside #filtering-system so it scrolls with the overlay
      if (results.parentElement !== panel) panel.appendChild(results);
    } else {
      // Desktop: body child, positioned fixed on the left via CSS
      if (results.parentElement !== document.body) document.body.appendChild(results);
    }
  }
  mobileMQ.addEventListener('change', placeResults);
  placeResults();

  // ── constants ───────────────────────────────────────────────────────────────
  const AREA_MIN = 20, AREA_MAX = 1300;
  if (rangeMin) { rangeMin.min = AREA_MIN; rangeMin.max = AREA_MAX; }
  if (rangeMax) { rangeMax.min = AREA_MIN; rangeMax.max = AREA_MAX; }

  const UI_TYPE_MAP = {
    'horeca': 'horeca',
    'living lab': 'living lab',
    'living-lab': 'living lab',
    'office': 'office',
    'kantoor': 'office',
    'kantor': 'office',
    'maakateliers': 'maakateliers'
  };

  const normAvailability = (v) => {
    const s = String(v || '').toLowerCase().replace(/\s+/g, '');
    if (s === 'verhuurd' || s === 'leased') return 'leased';
    if (s === 'gereserveerd' || s === 'reserved') return 'reserved';
    if (s === 'beschikbaar' || s === 'available') return 'available';
    if (s === 'sold') return 'sold';
    return s;
  };

  // ── helpers ─────────────────────────────────────────────────────────────────
  function bindAreaValueInputs() {
    let minTimer = null;
    let maxTimer = null;

    // helper: clamps + updates slider + UI + filtering
    function commitAreaValue(which, value) {
      const v = parseInt(value, 10);
      if (!Number.isFinite(v)) return;

      if (which === 'min') {
        rangeMin.value = Math.max(AREA_MIN, Math.min(v, AREA_MAX));
      } else {
        rangeMax.value = Math.max(AREA_MIN, Math.min(v, AREA_MAX));
      }

      updateRangeUI();

      // ✅ trigger re-filtering after typing stops
      if (typeof applyFilters === 'function') {
        applyFilters();
      } else if (typeof updateResults === 'function') {
        updateResults();
      } else {
        // fallback: simulate slider input event if that’s what drives your filter
        rangeMin.dispatchEvent(new Event('input'));
        rangeMax.dispatchEvent(new Event('input'));
      }
    }

    if (rangeMinVal) {
      rangeMinVal.contentEditable = true;
      rangeMinVal.addEventListener('input', () => {
        clearTimeout(minTimer);
        minTimer = setTimeout(() => {
          commitAreaValue('min', rangeMinVal.textContent);
        }, 600); // adjust delay (ms) as desired
      });
    }

    if (rangeMaxVal) {
      rangeMaxVal.contentEditable = true;
      rangeMaxVal.addEventListener('input', () => {
        clearTimeout(maxTimer);
        maxTimer = setTimeout(() => {
          commitAreaValue('max', rangeMaxVal.textContent);
        }, 600);
      });
    }
  }



  function getSelectedTypes() {
    const raw = typeChecks
      .filter(cb => cb.checked)
      .map(cb => cb.closest('.filter-type')?.dataset?.type || '');
    return new Set(
      raw.map(s => UI_TYPE_MAP[String(s).toLowerCase()] ?? String(s).toLowerCase()).filter(Boolean)
    );
  }

  function getSelectedStatuses() {
    return new Set(
      statusChecks.filter(cb => cb.checked)
        .map(cb => (cb.id || cb.value || '').toLowerCase())
    );
  }

  function getAreaRange() {
    let min = parseInt(rangeMin?.value ?? AREA_MIN, 10);
    let max = parseInt(rangeMax?.value ?? AREA_MAX, 10);
    if (!Number.isFinite(min)) min = AREA_MIN;
    if (!Number.isFinite(max)) max = AREA_MAX;
    if (min > max) [min, max] = [max, min];
    min = Math.max(AREA_MIN, Math.min(min, AREA_MAX));
    max = Math.max(AREA_MIN, Math.min(max, AREA_MAX));
    return { min, max };
  }

  function updateRangeUI() {
    const { min, max } = getAreaRange();
    if (rangeMinVal) rangeMinVal.textContent = String(min);
    if (rangeMaxVal) rangeMaxVal.textContent = String(max);
    if (sliderRange) {
      const leftPct = ((min - AREA_MIN) / (AREA_MAX - AREA_MIN)) * 100;
      const rightPct = 100 - ((max - AREA_MIN) / (AREA_MAX - AREA_MIN)) * 100;
      sliderRange.style.left = leftPct + '%';
      sliderRange.style.right = rightPct + '%';
    }
  }

  function allUnitRows() {
    if (!unitsLayer?.unitDataMap) return [];
    return Array.from(unitsLayer.unitDataMap.values());
  }

  function setSelectedCardByUnit(unitNumber, { scroll = true } = {}) {
    results.querySelectorAll('.unit-card.selected').forEach(el => el.classList.remove('selected'));
    if (!unitNumber) return;
    const q = CSS?.escape ? `.unit-card[data-unit="${CSS.escape(String(unitNumber))}"]`
      : `.unit-card[data-unit="${String(unitNumber)}"]`;
    const sel = results.querySelector(q) || results.querySelector(`.unit-card[data-unit="${String(unitNumber)}"]`);
    if (sel) {
      sel.classList.add('selected');
      if (scroll) sel.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // 3D → UI sync (mesh click selects the matching card) — bind once globally
  if (!window.__DEPOT_BOUND_UNIT_SELECT__) {
    window.__DEPOT_BOUND_UNIT_SELECT__ = true;
    document.addEventListener('unit-select', (e) => {
      const id = e.detail?.unitNumber ? String(e.detail.unitNumber) : null;
      setSelectedCardByUnit(id, { scroll: !!id });
    });
  }

  function renderResults(rows) {
    results.innerHTML = '';

    // Header (stays outside scrollable area on desktop)
    const hdr = document.createElement('div');
    hdr.className = 'filter-title results-title';
    hdr.id = 'results-title';
    const lang0 = window.currentLang || 'nl';
    hdr.textContent = window.t?.[lang0]?.matchingUnits || 'matching units:';
    results.appendChild(hdr);

    // Scrollable cards container
    const cardsWrap = document.createElement('div');
    cardsWrap.className = 'results-cards';
    results.appendChild(cardsWrap);

    if (!rows.length) {
      cardsWrap.innerHTML = `<div class="no-results">${(window.t?.[lang0]?.noResults) || 'Geen resultaten'}</div>`;
      return;
    }

    const lang = lang0;
    const labelLevel = (window.t?.[lang]?.details?.level) || (lang === 'nl' ? 'niveau' : 'level');

    const groups = new Map();
    for (const d of rows) {
      const lvl = Number.isFinite(parseInt(d.level, 10)) ? parseInt(d.level, 10) : -999;
      if (!groups.has(lvl)) groups.set(lvl, []);
      groups.get(lvl).push(d);
    }
    const sortedLevels = Array.from(groups.keys()).sort((a, b) => a - b);
    const frag = document.createDocumentFragment();

    for (const lvl of sortedLevels) {
      const groupRows = groups.get(lvl);

      groupRows.sort((a, b) => {
        const na = parseInt(String(a.number ?? '').replace(/\D+/g, '') || '0', 10);
        const nb = parseInt(String(b.number ?? '').replace(/\D+/g, '') || '0', 10);
        return na - nb;
      });

      const header = document.createElement('div');
      header.className = 'level-header';
      header.dataset.level = String(lvl);
      header.textContent = `${labelLevel} ${lvl === -999 ? '—' : lvl}`;
      frag.appendChild(header);

      const groupWrap = document.createElement('div');
      groupWrap.className = 'level-group';
      groupWrap.dataset.level = String(lvl);
      frag.appendChild(groupWrap);

      for (const d of groupRows) {
        const unitNumber = d.number || "-";
        const priceStr = formatPrice(d.price, d.price_mode, lang);
        const areaStr = (d.area != null && d.area !== '') ? `${d.area} m²` : '—';
        const typeClass = String(d.type || '').toLowerCase().replace(/\s+/g, '-');
        const imgUrl = d.overviewimage || d.overviewImage || '';

        // ▼ NEW localized bits
        const nameText = localizedName(d, lang);
        const availClass = normAvailabilityCode(d.availability);
        const availText = availabilityLabel(d.availability, lang, window.t);

        const card = document.createElement('div');
        card.className = `unit-card ${typeClass}`;
        card.dataset.unit = unitNumber;           // keep for later refresh

        const thumbHTML = imgUrl ? `<img src="${imgUrl}" alt="${nameText} overview">` : '';

        card.innerHTML = `
        <div class="uc-wrapper">
          <div class="uc-thumb${imgUrl ? '' : ' is-empty'}">
            ${thumbHTML}
          </div>
          <div class="uc-info">
            <div class="uc-title">
              <span class="uc-name">${nameText}</span>
              <span class="uc-number">${unitNumber}${d.level != null ? `.L${d.level}` : ''}</span>
            </div>
            <div class="uc-meta">
              <span class="uc-price">${priceStr}</span><br>
              <span class="uc-area">${areaStr}</span><br>
              <span class="availability ${availClass}">${availText}</span><br>
            </div>
          </div>
        </div>
      `;

        // Card → 3D + immediate highlight
        // helper matches your CSS breakpoint exactly
        const isMobile = () => window.matchMedia('(max-width: 768px)').matches;

        // inside wireFilteringUI -> renderResults -> card.addEventListener('click', ...)
        card.addEventListener('click', () => {
          try { postToParent({ type: 'IFRAME_UNIT_SELECTED' }); } catch { }
          const targetLevel = parseInt(d.level ?? '0', 10);
          if (Number.isFinite(targetLevel)) {
            proposalLayer?.setLevel?.(targetLevel);
            unitsLayer?.setLevel?.(targetLevel);
            if (typeof setLevelUIActive === 'function') setLevelUIActive(targetLevel);
          }

          const m = unitsLayer?.pickables?.find(mesh =>
            String(mesh.unitNumber || '') === String(unitNumber)
          );

          if (m) {
            // clear previous
            for (const obj of unitsLayer._selected) {
              obj.selected = false;
              obj.material.opacity = (obj._baseOpacity ?? 0.45);
            }
            unitsLayer._selected.clear();

            // select mesh
            unitsLayer._selected.add(m);
            m.selected = true;
            m.material.opacity = (m._selectedOpacity ?? 0.9);

            // ✅ on desktop/tablet: open details
            if (!isMobile()) {
              unitsLayer._renderUnitDetails?.(d, m.unitNumber, m.level);
              document.getElementById('details-display')?.classList.add('is-open');
            }

            // fly to mesh
            const center = unitsLayer?._meshCenterLngLat?.(m);
            const mapRef = unitsLayer?.map || map;
            if (center && mapRef) {
              const targetZoom = (window.matchMedia && window.matchMedia('(max-width: 768px)').matches)
                ? 18.6   // ← lighter zoom-in on mobile
                : 19.5;

              mapRef.easeTo({
                center,
                zoom: targetZoom,
                duration: 2000,
                easing: t => 1 - Math.pow(1 - t, 3),
                essential: true
              });
            }



            setSelectedCardByUnit(unitNumber, { scroll: true });
            document.dispatchEvent(new CustomEvent('unit-select', { detail: { unitNumber } }));

            // 🚫 on mobile: close filtering (no details auto-open)
            if (isMobile()) {
              while (document.getElementById('filtering-system')?.classList.contains('is-open')) {
                document.getElementById('filtering-system')?.classList.remove('is-open');
              }
            }
          }
        });


        groupWrap.appendChild(card);
      }
    }

    cardsWrap.appendChild(frag);

    // keep selection if a mesh is already selected
    const selectedMesh = (unitsLayer && unitsLayer._selected && unitsLayer._selected.values().next().value) || null;
    if (selectedMesh?.unitNumber) {
      setSelectedCardByUnit(selectedMesh.unitNumber, { scroll: false });
    }
  }

  function applyFilters() {
    const typeSet = getSelectedTypes();
    const statusSet = getSelectedStatuses();
    const { min: areaMin, max: areaMax } = getAreaRange();

    // drive the 3D visibility
    unitsLayer?.setUIFilters?.({ typeSet, statusSet, areaMin, areaMax });

    // render cards from full DB, filtered by UI selection
    const rows = allUnitRows().filter(d => {
      const dType = String(d.type || '').trim().toLowerCase();
      const typeOK = typeSet.size === 0 || typeSet.has(dType);

      const avOK = (statusSet.size === 0) || statusSet.has(normAvailability(d.availability));

      const a = (typeof d.area === 'number') ? d.area : parseFloat(d.area);
      const areaOK = Number.isFinite(a) && a >= areaMin && a <= areaMax;

      return typeOK && avOK && areaOK;
    });

    renderResults(rows);
  }

  function resetFilters() {
    typeChecks.forEach(cb => { cb.checked = false; cb.closest('.filter-type')?.setAttribute('aria-checked', 'false'); });
    statusChecks.forEach(cb => { cb.checked = false; });
    if (rangeMin) rangeMin.value = String(AREA_MIN);
    if (rangeMax) rangeMax.value = String(AREA_MAX);
    updateRangeUI();
    bindAreaValueInputs();
    applyFilters();
  }

  // ── events ──────────────────────────────────────────────────────────────────
  // make the whole colored type row act as a toggle (mouse + keyboard)
  typeRows.forEach(row => {
    const cb = row.querySelector('.type-checkbox');
    if (!cb) return;

    row.tabIndex = 0;
    row.setAttribute('role', 'checkbox');
    row.setAttribute('aria-checked', cb.checked ? 'true' : 'false');

    // clicking the checkbox itself will trigger 'change' below
    row.addEventListener('click', (ev) => {
      const label = row.querySelector('label');
      const clickedControl = (ev.target === cb) || (label && label.contains(ev.target));
      if (clickedControl) return;
      cb.checked = !cb.checked;
      cb.dispatchEvent(new Event('change', { bubbles: true }));
    });

    row.addEventListener('keydown', (ev) => {
      if (ev.key === ' ' || ev.key === 'Enter') {
        ev.preventDefault();
        cb.checked = !cb.checked;
        cb.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  });

  // keep aria state + apply on checkbox change
  typeChecks.forEach(cb => cb.addEventListener('change', () => {
    const row = cb.closest('.filter-type');
    if (row) row.setAttribute('aria-checked', cb.checked ? 'true' : 'false');
    applyFilters();
  }));

  statusChecks.forEach(cb => cb.addEventListener('change', applyFilters));
  if (rangeMin) rangeMin.addEventListener('input', () => { updateRangeUI(); applyFilters(); });
  if (rangeMax) rangeMax.addEventListener('input', () => { updateRangeUI(); applyFilters(); });
  if (resetBtn) resetBtn.addEventListener('click', resetFilters);
  if (showAllBtn) showAllBtn.addEventListener('click', resetFilters);

  // patch setLevel so changing levels re-applies filters immediately
  if (unitsLayer && typeof unitsLayer.setLevel === 'function' && !unitsLayer._filterSetLevelPatched) {
    const originalSetLevel = unitsLayer.setLevel.bind(unitsLayer);
    unitsLayer.setLevel = (lvl) => { originalSetLevel(lvl); applyFilters(); };
    unitsLayer._filterSetLevelPatched = true;
  }

  // initial
  function startWhenReady() {
    if (unitsLayer?.modelLoaded && unitsLayer?.unitDataMap) {
      resetFilters();
      return true;
    }
    return false;
  }
  if (!startWhenReady()) {
    const iv = setInterval(() => { if (startWhenReady()) clearInterval(iv); }, 200);
  }
}






// Optional API if you want to call elsewhere
export function setCurrentLevel(level) {
  proposalLayer?.setLevel?.(level ?? null);
  unitsLayer?.setLevel?.(level ?? null);
  zoomReset(); // ← home
}
export function switchLevel(delta) {
  proposalLayer?.stepLevel?.(delta);
  unitsLayer?.stepLevel?.(delta);
  zoomReset(); // ← home
}



/* ============================================================================
   NEW: Camera pins manager
   - HTML Mapbox Markers that always face the camera
   - Support {lngLat:[lng,lat], height:m, level:number|'all', media:{type:'image'|'video', src, poster?, alt?}}
   ============================================================================ */

const CAMERA_ICON_URL = (location.hostname === 'localhost' || location.hostname === '127.0.0.1')
  ? './CameraIcon.svg'
  : 'https://publictwin.info/UtrechtDEPOT/content/CameraIcon.svg';

// Example data — replace with your actual points.
// Height is in meters above ground; level can be number (0..6) or 'all'.
const CAMERA_MARKERS = [
  {
    id: 'cam-01',
    lngLat: [5.1097888, 52.0747881],
    height: 1,
    level: "all",
    media: { type: 'image', src: 'https://publictwin.info/UtrechtDEPOT/content/Images/heroshot_01-min.jpg', alt: 'View from the canal' }
  },
  {
    id: 'cam-02',
    lngLat: [5.1078635, 52.0751642],
    height: 1,
    level: "all",
    media: { type: 'image', src: 'https://publictwin.info/UtrechtDEPOT/content/Images/heroshot_02-min.jpg', alt: 'View from the plaza' }
  },
  {
    id: 'cam-03',
    lngLat: [5.1090637, 52.0748596],
    height: 1,
    level: 0,
    media: { type: 'video', src: 'https://publictwin.info/UtrechtDEPOT/content/Videos/depot_animation_01_finalreview_compressed.mp4', poster: 'https://publictwin.info/UtrechtDEPOT/content/Videos/depot_animation_01_view-min.png' }
  },
  {
    id: 'cam-04',
    lngLat: [5.1090899, 52.0747332],
    height: 1,
    level: 0,
    media: { type: 'video', src: 'https://publictwin.info/UtrechtDEPOT/content/Videos/depot_animation_02_finalreview_compressed.mp4', poster: 'https://publictwin.info/UtrechtDEPOT/content/Videos/depot_animation_02_view-min.png' }
  },
  {
    id: 'cam-05',
    lngLat: [5.108551, 52.0750066],
    height: 14.5,
    level: [2, 3],
    media: { type: 'video', src: 'https://publictwin.info/UtrechtDEPOT/content/Videos/depot_animation_03_finalreview_compressed.mp4', poster: 'https://publictwin.info/UtrechtDEPOT/content/Videos/depot_animation_03_view-min.png' }
  },
  {
    id: 'cam-06',
    lngLat: [5.1089834, 52.0751387],
    height: 14.5,
    level: [2, 3],
    media: { type: 'video', src: 'https://publictwin.info/UtrechtDEPOT/content/Videos/depot_animation_05_finalreview_compressed.mp4', poster: 'https://publictwin.info/UtrechtDEPOT/content/Videos/depot_animation_05_view-min.png' }
  },
  {
    id: 'cam-07',
    lngLat: [5.1086925, 52.0752044],
    height: 42.5,
    level: 6,
    media: { type: 'video', src: 'https://publictwin.info/UtrechtDEPOT/content/Videos/depot_animation_04_finalreview_compressed.mp4', poster: 'https://publictwin.info/UtrechtDEPOT/content/Videos/depot_animation_04_view-min.png' }
  },
];

function openMediaViewer(payload) {
  const viewer = document.getElementById('media-viewer');
  if (!viewer) return;
  const box = viewer.querySelector('.mv-box');
  if (!box) return;

  box.innerHTML = '';

  if (payload?.type === 'video') {
    const v = document.createElement('video');
    v.src = payload.src;
    v.controls = true;
    v.autoplay = true;
    v.loop = true;           // request autoplay
    v.playsInline = true;          // iOS inline playback
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    v.preload = 'metadata';
    if (payload.poster) v.poster = payload.poster;
    box.appendChild(v);

    // Try to autoplay WITH sound (because we're in a click handler’s call stack)
    const tryPlay = () => v.play().catch(async () => {
      // Fallback: muted autoplay (universally allowed)
      v.muted = true;
      v.setAttribute('muted', '');
      try { await v.play(); } catch { }
    });

    // If metadata already available, play immediately; otherwise wait
    if (v.readyState >= 2) {
      tryPlay();
    } else {
      const onMeta = () => { v.removeEventListener('loadedmetadata', onMeta); tryPlay(); };
      v.addEventListener('loadedmetadata', onMeta);
    }
  } else {
    const img = document.createElement('img');
    img.src = payload.src;
    img.alt = 'media';
    box.appendChild(img);
  }

  viewer.classList.add('is-open');
  viewer.setAttribute('aria-hidden', 'false');
}

function closeMediaViewer() {
  const overlay = document.getElementById('media-viewer');
  const box = overlay?.querySelector('.mv-box');
  if (!overlay || !box) return;
  box.innerHTML = '';
  overlay.classList.remove('is-open');
  overlay.setAttribute('aria-hidden', 'true');
}

// wire close actions once DOM exists
(function wireMediaViewerClose() {
  const overlay = document.getElementById('media-viewer');
  const closeBtn = overlay?.querySelector('.mv-close');
  closeBtn?.addEventListener('click', closeMediaViewer);

  // click outside content area closes
  overlay?.addEventListener('click', (e) => {
    const isBox = e.target.closest('.mv-box');
    if (!isBox) closeMediaViewer();
  });
  // ESC closes
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeMediaViewer();
  }, { passive: true });
})();

// Create / manage pins
function createCameraPins(markers) {
  const pins = [];

  const mkEl = () => {
    const el = document.createElement('div');
    el.className = 'camera-pin';
    el.innerHTML = `<img class="icon" alt="" src="${CAMERA_ICON_URL}">`;
    return el;
  };

  // build markers
  for (const def of markers) {
    const el = mkEl();
    el.addEventListener('click', () => openMediaViewer(def.media));
    const marker = new mapboxgl.Marker({ element: el, anchor: 'center', offset: [0, 0] })
      .setLngLat(def.lngLat)
      .addTo(map);
    pins.push({ def, marker, el });
  }

  // pixels-per-meter helper
  const ppmAt = (lat, zoom) => {
    const metersPerPixel = 156543.03392 * Math.cos(lat * Math.PI / 180) / Math.pow(2, zoom);
    return 1 / metersPerPixel;
  };

  function updateOffsets() {
    const pitchRad = map.getPitch() * Math.PI / 180;
    const zoom = map.getZoom();
    for (const p of pins) {
      // simple approximation: altitude projects to screen Y by sin(pitch)
      const ppm = ppmAt(p.def.lngLat[1], zoom);
      const y = - (p.def.height || 0) * ppm * Math.sin(pitchRad);
      p.marker.setOffset([0, y]);
    }
  }

  // helper: flexible level spec matcher
  function isMarkerVisibleForLevel(levelSpec, curLevel) {
    // When no level is selected in UI: only "all" stays visible (your current behavior)
    if (curLevel === null || curLevel === undefined) {
      return levelSpec === 'all' || levelSpec === undefined || levelSpec === null;
    }

    // Always visible
    if (levelSpec === 'all' || levelSpec === undefined || levelSpec === null) return true;

    // Array of levels: [0,1,2]
    if (Array.isArray(levelSpec)) {
      return levelSpec.some(n => Number(n) === Number(curLevel));
    }

    // Range object: {min: 1, max: 4} (inclusive)
    if (typeof levelSpec === 'object') {
      const min = Number.isFinite(levelSpec.min) ? Number(levelSpec.min) : -Infinity;
      const max = Number.isFinite(levelSpec.max) ? Number(levelSpec.max) : +Infinity;
      return Number(curLevel) >= min && Number(curLevel) <= max;
    }

    // Comma/range string DSL: '1-3,5,7' (spaces allowed)
    if (typeof levelSpec === 'string') {
      const clean = levelSpec.replace(/\s+/g, '');
      if (clean.toLowerCase() === 'all') return true;
      const parts = clean.split(',');
      for (const part of parts) {
        if (/^\d+$/.test(part)) {
          if (Number(part) === Number(curLevel)) return true;
        } else {
          const m = /^(\d+)-(\d+)$/.exec(part);
          if (m) {
            const a = Number(m[1]), b = Number(m[2]);
            const lo = Math.min(a, b), hi = Math.max(a, b);
            if (Number(curLevel) >= lo && Number(curLevel) <= hi) return true;
          }
        }
      }
      return false;
    }

    // Single number
    return Number(levelSpec) === Number(curLevel);
  }

  function updateVisibility() {
    const curLevel = proposalLayer?.currentLevel ?? unitsLayer?.currentLevel ?? null;
    for (const p of pins) {
      const show = isMarkerVisibleForLevel(p.def.level, curLevel);
      p.el.style.display = show ? 'grid' : 'none';
    }
  }


  // keep offsets in sync with camera
  map.on('move', updateOffsets);
  map.on('resize', updateOffsets);

  // patch layer level methods so visibility updates instantly
  const patch = (layer) => {
    if (!layer || layer._cameraPinsPatched) return;
    if (typeof layer.setLevel === 'function') {
      const orig = layer.setLevel.bind(layer);
      layer.setLevel = (lvl) => { const r = orig(lvl); updateVisibility(); return r; };
    }
    if (typeof layer.showAllLevels === 'function') {
      const orig = layer.showAllLevels.bind(layer);
      layer.showAllLevels = (...a) => { const r = orig(...a); updateVisibility(); return r; };
    }
    if (typeof layer.stepLevel === 'function') {
      const orig = layer.stepLevel.bind(layer);
      layer.stepLevel = (d) => { const r = orig(d); updateVisibility(); return r; };
    }
    layer._cameraPinsPatched = true;
  };

  // initial sync + patches
  updateOffsets();
  updateVisibility();
  patch(proposalLayer);
  patch(unitsLayer);

  return { pins, updateOffsets, updateVisibility };
}





// ───────────────────────────────────────────────────────────────────────────────
// Map load: add layers + clip + UI
// ───────────────────────────────────────────────────────────────────────────────
map.on('load', () => {
  // console.log('theme:', map.getConfigProperty('basemap', 'theme'));
  // console.log('lightPreset:', map.getConfigProperty('basemap', 'lightPreset'));

  // proposal (hover)
  proposalLayer = createGLTFLayer(
    'models/UtrechtDepot/UtrechtDepot_Proposal.glb',
    'proposal',
    [5.10935, 52.074915],
    { interaction: 'none' }
  );
  map.addLayer(proposalLayer);

  // context (click select)
  contextLayer = createGLTFLayer(
    'models/UtrechtDepot/UtrechtDepot_Context.glb',
    'context',
    [5.10935, 52.074915],
    {
      interaction: 'none',
      multiSelect: false,
      onClick: (hits, e, layer) => {
        if (!hits.length) return;
        // console.log('Selected:', hits[0].object.name);
      }
    }
  );
  map.addLayer(contextLayer);

  // park layer (click select)
  parkLayer = createGLTFLayer(
    'models/UtrechtDepot/UtrechtDepot_Park.glb',
    'park',
    [5.10935, 52.074915],
    {
      interaction: 'none',
      multiSelect: false,
      onClick: (hits, e, layer) => {
        if (!hits.length) return;
        // console.log('Selected:', hits[0].object.name);
      }
    }
  );
  map.addLayer(parkLayer);

  // units
  unitsLayer = createGLTFLayer(
    'models/UtrechtDepot/UtrechtDepot_Units_new.glb',
    'units',
    [5.10935, 52.074915],
    {
      interaction: 'unitsHover',
      multiSelect: false,
      levelFilterMode: 'eq',   // show only exact level
      unitDataUrl: 'https://publictwin.info/UtrechtDEPOT/php_scripts/readUnitsData.php',
      hoverStyle: 'opacity',
      baseOpacity: 0.45,
      hoverOpacity: 0.75,
      selectedOpacity: 0.9,
      // (optional) you can inspect what was hovered:
      onHover: (hit) => {
        if (hit?.object?.level != null) {
          // Example: show level in console
          // console.log('Hovered units level:', hit.object.level);
          // console.log('Hovered unit:', hit.object);
        }
      }
    }
  );
  map.addLayer(unitsLayer);

  // clip layer (eraser)
  map.addSource('eraser', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [[
              [5.1093218547098616, 52.0761552550843],
              [5.10832289035406, 52.07655209212518],
              [5.105983851861879, 52.07623013026759],
              [5.105654924574253, 52.0761253049759],
              [5.105484369683467, 52.075885703384444],
              [5.10758714812971, 52.06753626674788],
              [5.108025717847141, 52.06718428376382],
              [5.108403375103677, 52.06707194818483],
              [5.110852056024726, 52.067296619060215],
              [5.111461180632006, 52.06785080238643],
              [5.1093218547098616, 52.0761552550843]
            ]]
          }
        }
      ]
    }
  });

  map.addLayer({
    id: 'eraser',
    type: 'clip',
    source: 'eraser',
    layout: { 'clip-layer-types': ['symbol', 'model'] }
  });

  // UI
  wireLevelUI();
  wireFilteringUI();

  // Desktop: position #details-display below #filtering-system on the right
  (function wireDesktopDetailsPosition() {
    const fp = document.getElementById('filtering-system');
    const dp = document.getElementById('details-display');
    if (!fp || !dp) return;

    const mobileQuery = window.matchMedia(
      '(max-width: 768px), (max-width: 1024px) and (orientation: portrait)'
    );

    function update() {
      if (mobileQuery.matches) {
        dp.style.top = '';
        const ud = dp.querySelector('#unit-details');
        if (ud) ud.style.maxHeight = '';
        return;
      }
      const rect = fp.getBoundingClientRect();
      const topPos = rect.bottom + 8;
      dp.style.top = topPos + 'px';
      // Align bottom of details with actual bottom of left panel
      const leftPanel = document.getElementById('filter-results');
      const targetBottom = leftPanel
        ? leftPanel.getBoundingClientRect().bottom
        : rect.bottom;
      const ud = dp.querySelector('#unit-details');
      if (ud) ud.style.maxHeight = Math.max(0, targetBottom - topPos) + 'px';
    }

    const ro = new ResizeObserver(update);
    ro.observe(fp);
    // Also watch left panel so we recalc when its content loads/changes
    const lp = document.getElementById('filter-results');
    if (lp) ro.observe(lp);
    window.addEventListener('resize', update);
    mobileQuery.addEventListener('change', update);
    // Re-run when details panel opens
    new MutationObserver(update).observe(dp, { attributes: true, attributeFilter: ['class'] });
    update();
  })();

  (function initCameraPins() {
    // If you want to feed data dynamically later, replace CAMERA_MARKERS.
    window.__cameraPinsManager = createCameraPins(CAMERA_MARKERS);

    // Also reflect level changes triggered by the level UI buttons:
    const refresh = () => window.__cameraPinsManager?.updateVisibility?.();
    map.on('moveend', refresh); // safe extra guard after big view changes
  })();

  (function initProposalFloatingLevelControl() {
    const elId = 'bbox-level-add-subtract';

    // Create (or reuse) the control element
    let ctrl = document.getElementById(elId);
    if (!ctrl) {
      ctrl = document.createElement('div');
      ctrl.id = elId;
      ctrl.innerHTML = `
      <button type="button" class="plus moveUp"  aria-label="Level up">▲</button>
      <div class="bbox-level-add-subtract-divider"></div>
      <button type="button" class="minus" aria-label="Level down">▼</button>
    `;
    }

    // Use a Mapbox Marker so positioning stays correct inside iframes / HiDPI / CSS scaling
    // We’ll adjust its pixel offset on each update to create the vertical travel effect.
    let marker = new mapboxgl.Marker({
      element: ctrl,
      anchor: 'center',
      offset: [0, 0],
    });

    // pixels the control moves per level (screen Y)
    const PIXELS_PER_LEVEL = 26;

    function ensureAttached() {
      // Add only once, and only when we have an anchor
      if (!marker._map && proposalLayer?._bboxAnchorLngLat) {
        marker.setLngLat(proposalLayer._bboxAnchorLngLat).addTo(map);
        ctrl.style.display = 'block';
        ctrl.style.pointerEvents = 'auto';
      }
    }

    function updateCtrlPosition() {
      // Hide while not ready
      if (!proposalLayer?.modelLoaded) {
        ctrl.style.display = 'none';
        return;
      }
      // Compute the anchor (center of full proposal bbox) once
      if (!proposalLayer._bboxAnchorLngLat) {
        proposalLayer._bboxAnchorLngLat = proposalLayer._computeProposalAnchorLngLat?.() || null;
      }
      const anchor = proposalLayer._bboxAnchorLngLat;
      if (!anchor) {
        ctrl.style.display = 'none';
        return;
      }

      ensureAttached();

      // Vertical offset per level (higher level => higher on screen)
      const minL = Number.isFinite(proposalLayer?.minLevel) ? proposalLayer.minLevel : 0;
      const curL = proposalLayer?.currentLevel;
      let offsetY = 0;
      if (Number.isFinite(curL)) {
        offsetY = -(curL - minL) * PIXELS_PER_LEVEL;
      }

      // Keep marker anchored at the model center, then nudge by pixel offset
      marker.setLngLat(anchor);
      marker.setOffset([0, offsetY]);

      ctrl.style.display = 'block';
    }

    // Wire buttons
    ctrl.querySelector('.plus')?.addEventListener('click', () => {
      proposalLayer?.stepLevel?.(+1);
      unitsLayer?.stepLevel?.(+1);
      const cur = proposalLayer?.currentLevel ?? null;
      setLevelUIActive?.(cur);
      selectFilterForLevel(cur);
      updateCtrlPosition();
      zoomReset();
    });
    ctrl.querySelector('.minus')?.addEventListener('click', () => {
      proposalLayer?.stepLevel?.(-1);
      unitsLayer?.stepLevel?.(-1);
      const cur = proposalLayer?.currentLevel ?? null;
      setLevelUIActive?.(cur);
      selectFilterForLevel(cur);
      updateCtrlPosition();
      zoomReset();
    });


    // Reposition with map changes
    // 'move' covers pan/zoom/rotate/pitch; 'resize' covers container resizes/iframe resizes;
    // we also keep 'render' if you want ultra-smooth tracking, but 'move' is usually enough.
    const onMapMove = () => updateCtrlPosition();
    map.on('move', onMapMove);
    map.on('resize', onMapMove);
    map.on('render', onMapMove);

    // Patch proposal layer level changes so we re-offset the marker immediately
    (function patchLevelHooks() {
      if (!proposalLayer || proposalLayer._bboxCtrlPatched) return;

      const patch = (methodName) => {
        if (typeof proposalLayer[methodName] !== 'function') return;
        const orig = proposalLayer[methodName].bind(proposalLayer);
        proposalLayer[methodName] = (...args) => {
          const r = orig(...args);
          updateCtrlPosition();
          return r;
        };
      };

      patch('setLevel');
      patch('showAllLevels');
      patch('_applyLevelFilter');

      proposalLayer._bboxCtrlPatched = true;
    })();

    // Initial draw (when ready)
    updateCtrlPosition();

    initGeoProbe();
  })();




});

// resize
window.addEventListener('resize', () => map.resize());


const zoomAllBtn = document.querySelector('#level-show-all .zoom-all');
// we’ll also grab the “all” element to reuse its existing behavior
const showAllBtnRef = document.querySelector('#level-show-all .show-all');

// helper: mobile OR portrait tablet
function isMobileOrPortraitTablet() {
  return window.matchMedia('(max-width: 768px), (min-width: 769px) and (max-width: 1024px) and (orientation: portrait)').matches;
}

zoomAllBtn.addEventListener('click', function () {
  if (isMobileOrPortraitTablet()) {
    // On mobile & portrait tablet: trigger the existing “all” logic (which also does zoomReset)
    if (showAllBtnRef) {
      showAllBtnRef.click();
      return;
    }
    // Fallback if the element isn’t found: call the underlying functions directly
    proposalLayer?.showAllLevels?.();
    unitsLayer?.showAllLevels?.();
    setLevelUIActive?.(null);
    zoomReset();
    return;
  }

  // Desktop & tablet landscape: keep current Home behavior (just zoom home)
  map.easeTo({
    center: cameraReset.center,
    zoom: cameraReset.zoom,
    pitch: cameraReset.pitch,
    bearing: cameraReset.bearing,
    duration: 2000,
    easing: t => t
  });
});





// ───────────────────────────────────────────────────────────────────────────────
// IFRAME-BASED MESSAGING (parent → Twin) with ACK/DONE
// ───────────────────────────────────────────────────────────────────────────────

let pendingFromParent = null; // { focusOrLevel, messageId } if we must defer

function zoomReset(duration = 1600) {
  map.easeTo({
    center: cameraReset.center,
    zoom: cameraReset.zoom,
    pitch: cameraReset.pitch,
    bearing: cameraReset.bearing,
    duration,
    easing: t => 1 - Math.pow(1 - t, 3),
    essential: true
  });
}


function setBothLevels(level /* number or null */) {
  if (level === null) {
    proposalLayer?.showAllLevels?.();
    unitsLayer?.showAllLevels?.();
    setLevelUIActive?.(null);
  } else {
    proposalLayer?.setLevel?.(level);
    unitsLayer?.setLevel?.(level);
    setLevelUIActive?.(level);
  }
  selectFilterForLevel(level);
}

const FOCUS_TO_LEVEL = { home: null, food: 0, lab: 2, office: 4 };

function resolveFocusToLevel(focusLike) {
  const f = String(focusLike).toLowerCase().trim();
  if (Object.prototype.hasOwnProperty.call(FOCUS_TO_LEVEL, f)) return { focus: f, level: FOCUS_TO_LEVEL[f] };
  // numeric fallback
  const n = parseInt(f, 10);
  return Number.isFinite(n) ? { focus: null, level: n } : { focus: null, level: null };
}

function applyFocusAction(resolved) {
  setBothLevels(resolved.level);
  zoomReset();
}

function modelsReady() {
  return !!(proposalLayer?.modelLoaded && unitsLayer?.modelLoaded);
}

function parentOrigin() {
  try { return document.referrer ? new URL(document.referrer).origin : '*'; }
  catch { return '*'; }
}
function postToParent(payload) {
  window.parent.postMessage(payload, parentOrigin() || '*');
}

window.addEventListener('message', (evt) => {
  const msg = evt.data;
  const messageId = (msg && typeof msg === 'object' && msg.messageId) || null;

  // ── Language change from parent ───────────────────────────────────────────
  if (msg && typeof msg === 'object' && msg.type === 'DEPOT_LANG') {
    
    // Optional origin allowlist (recommended for security)
    const REF_ORIGIN = (() => {
      try { return new URL(document.referrer).origin; } catch { return null; }
    })();

    const allowedParents = new Set([
      'https://publictwin.info',
      'https://www.publictwin.info',
      'https://het-depot.nl',
      'https://www.het-depot.nl',
      'http://localhost:3000',
      'http://localhost:5173',      // ← add Vite default port
      'http://127.0.0.1:5173',      // ← add 127.0.0.1 variant
      REF_ORIGIN                    // ← dynamic: whatever actually embedded us
    ].filter(Boolean));

    if (allowedParents.size && !allowedParents.has(evt.origin)) {
      console.warn('[Twin] Ignoring message from unexpected origin:', evt.origin, 'allowed:', [...allowedParents]);
      return;
    }

    // normalize + validate
    const code = String(msg.lang || '').toLowerCase();
    const lang = (code === 'nl' || code === 'en') ? code : null;

    // ACK immediately
    postToParent({
      type: 'DEPOT_ACK',
      inReplyTo: messageId,
      received: true,
      action: 'lang',
      lang: lang || code
    });

    if (!lang) {
      // DONE with failure if invalid code
      postToParent({
        type: 'DEPOT_DONE',
        inReplyTo: messageId,
        applied: false,
        action: 'lang',
        error: 'unsupported_language'
      });
      return;
    }

    // If DOM not ready yet, store and apply later (DOMContentLoaded hook above)
    if (typeof window.setAppLanguage !== 'function') {
      window.__pendingLang = lang;
    } else {
      window.setAppLanguage(lang);
    }

    // DONE success
    postToParent({
      type: 'DEPOT_DONE',
      inReplyTo: messageId,
      applied: true,
      action: 'lang',
      lang
    });
    return; // handled
  }

  // ── Show interest form on request from parent ────────────────────────────
  if (msg && typeof msg === 'object' && msg.type === 'DEPOT_SHOW_FORM') {
    const modal = document.getElementById('interested-modal');
    const form = document.getElementById('interest-form');
    if (modal && form) {
      // Clear any previous context for a fresh inquiry
      const m = form.querySelector('#message');
      if (m) { m.value = ''; m.dataset.autoprefill = '0'; }
      const statusEl = form.querySelector('#form-status');
      if (statusEl) { statusEl.textContent = ''; statusEl.classList.remove('is-processing', 'is-success', 'is-error'); }
      modal.classList.remove('hidden');
    }
    return;
  }

  // Normalize input to a "resolved" {focus, level}
  let resolved = null;
  if (msg && typeof msg === 'object' && (msg.type === 'DEPOT_FOCUS' || msg.type === 'DEPOT_INIT')) {
    resolved = resolveFocusToLevel(msg.focus);
  } else if (typeof msg === 'string' || Number.isFinite(msg)) {
    resolved = resolveFocusToLevel(msg);
  } else if (msg && typeof msg === 'object' && Number.isFinite(msg.level)) {
    resolved = { focus: null, level: msg.level };
  }

  if (!resolved) return;

  // ACK immediately: message received
  postToParent({
    type: 'DEPOT_ACK',
    inReplyTo: messageId,
    received: true,
    focus: resolved.focus,
    level: resolved.level
  });

  if (modelsReady()) {
    applyFocusAction(resolved);
    postToParent({
      type: 'DEPOT_DONE',
      inReplyTo: messageId,
      applied: true,
      focus: resolved.focus,
      level: resolved.level
    });
  } else {
    // defer until models are ready
    pendingFromParent = { focusOrLevel: resolved, messageId };
  }
}, { passive: true });

// When layers finish loading, flush any pending action and notify parent
(function waitForModelsThenNotify() {
  const iv = setInterval(() => {
    if (modelsReady()) {
      clearInterval(iv);

      if (pendingFromParent) {
        applyFocusAction(pendingFromParent.focusOrLevel);
        postToParent({
          type: 'DEPOT_DONE',
          inReplyTo: pendingFromParent.messageId,
          applied: true,
          focus: pendingFromParent.focusOrLevel.focus,
          level: pendingFromParent.focusOrLevel.level
        });
        pendingFromParent = null;
      }

      postToParent({ type: 'DEPOT_READY' });
    }
  }, 150);
})();

function closeFilterPanel() {
  document.getElementById('filtering-system')?.classList.remove('is-open');
}
function openDetailsPanel() {
  document.getElementById('details-display')?.classList.add('is-open');
}
function closeDetailsPanel() {
  document.getElementById('details-display')?.classList.remove('is-open');
}

// wire close buttons
document.querySelector('#filtering-system .close-btn')?.addEventListener('click', closeFilterPanel);
document.querySelector('#details-display .close-btn')?.addEventListener('click', closeDetailsPanel);

const filteringPanel = document.getElementById('filtering-system');
const filterToggleBtn = document.getElementById('filter-toggle-btn');


// When selecting unit via filter → close filter, open details
document.addEventListener('unit-select', (e) => {
  closeFilterPanel();
  if (e.detail?.unitNumber) openDetailsPanel();
});

// Show filter toggle button on mobile

function handleResize() {
  if (window.innerWidth <= 768) {
    filterToggleBtn.style.display = 'block';
  } else {
    filterToggleBtn.style.display = 'none';
  }
}
window.addEventListener('resize', handleResize);
handleResize();


filterToggleBtn?.addEventListener('click', () => {
  if (!filteringPanel) return;
  filteringPanel.classList.add('is-open');

  // Reset scroll position so it always opens at the top
  // Do it on next frame to avoid race with CSS transform
  requestAnimationFrame(() => {
    filteringPanel.scrollTop = 0;
    // also reset the document scroll just in case the panel’s not the scroller
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  });
});

// Also reset whenever you programmatically open the filter panel elsewhere:
function openFilterPanel() {
  filteringPanel?.classList.add('is-open');
  requestAnimationFrame(() => {
    filteringPanel.scrollTop = 0;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  });
}


// ───────────────────────────────────────────────────────────────────────────────
// Mobile landscape → ask user to rotate to portrait
// ───────────────────────────────────────────────────────────────────────────────
(function initRotateHint() {
  const id = 'rotate-hint';
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('div');
    el.id = id;
    el.innerHTML = `
      <div class="box">
        <div style="font-size:20px;margin-bottom:4px;">Best viewed in portrait</div>
        <div style="font-size:14px;opacity:.9;">Rotate your device for the full experience.</div>
      </div>`;
    document.body.appendChild(el);
  }
  const mq = window.matchMedia('(max-width: 1024px) and (orientation: landscape) and (pointer: coarse)');
  const update = () => { el.classList.toggle('is-open', mq.matches); };
  update();
  mq.addEventListener('change', update);
  window.addEventListener('resize', update, { passive: true });
})();










// ─────────────────────────────
// Simple single-button language toggle
// ─────────────────────────────
// ─────────────────────────────
// Language toggle + interest form modal
// ─────────────────────────────
document.addEventListener("DOMContentLoaded", () => {
  const langToggle = document.getElementById("lang-toggle");
  const interestedBtn = document.getElementById("interested-btn");
  const modal = document.getElementById("interested-modal");
  const closeModalBtn = document.querySelector(".close-modal");
  const form = document.getElementById("interest-form");
  const modalTitle = document.getElementById("modal-title");
  // --- Interest context (what unit is the user asking about?)
  let interestContext = null; // { unitNumber, level, unitName, area, price, type } or null

  function setFormEnabled(enabled) {
    Array.from(form.elements).forEach(el => { el.disabled = !enabled; });
  }

  function setFormStatus(kind /* 'processing'|'success'|'error' */, lang) {
    const el = form.querySelector('#form-status');
    if (!el) return;
    const msg = (t[lang]?.status?.[kind]) || '';
    el.textContent = msg;

    // toggle simple state classes (style these in CSS if you like)
    el.classList.remove('is-processing', 'is-success', 'is-error');
    if (kind === 'processing') el.classList.add('is-processing');
    if (kind === 'success') el.classList.add('is-success');
    if (kind === 'error') el.classList.add('is-error');
  }


  // Hidden fields for server-side
  const hidUnit = document.createElement('input');
  hidUnit.type = 'hidden'; hidUnit.name = 'unit';
  const hidLevel = document.createElement('input');
  hidLevel.type = 'hidden'; hidLevel.name = 'level';
  const hidUName = document.createElement('input');
  hidUName.type = 'hidden'; hidUName.name = 'unitName';
  const hidLang = document.createElement('input');
  hidLang.type = 'hidden'; hidLang.name = 'language';

  form.appendChild(hidUnit);
  form.appendChild(hidLevel);
  form.appendChild(hidUName);
  form.appendChild(hidLang);

  // Helper: set/clear + localize the default message when a unit is selected
  function setInterestContext(ctx) {
    interestContext = ctx; // or null
    const lang = window.currentLang || 'nl';
    hidLang.value = lang;

    if (!interestContext) {
      // clear hidden fields + leave message as-is
      hidUnit.value = '';
      hidLevel.value = '';
      hidUName.value = '';
      return;
    }

    const { unitNumber, level, unitName, area, price, type } = interestContext;
    hidUnit.value = String(unitNumber ?? '');
    hidLevel.value = String(level ?? '');
    hidUName.value = String(unitName ?? '');

    // Put a localized “header” into the message box (prepend; user can edit freely)
    const m = form.querySelector('#message');
    if (m) {
      const headNL = `Interesse in unit ${unitNumber}${level != null ? ` (niveau ${level})` : ''} - ${unitName}`;
      const headEN = `Interested in unit ${unitNumber}${level != null ? ` (level ${level})` : ''} - ${unitName}`;
      const specsNL = [
        type ? `type: ${type}` : null,
        area ? `oppervlakte: ${area} m²` : null,
        price ? `prijs: €${Number(price).toLocaleString('nl-NL', { maximumFractionDigits: 0 })}` : null,
      ].filter(Boolean).join(' · ');
      const specsEN = [
        type ? `type: ${type}` : null,
        area ? `area: ${area} m²` : null,
        price ? `price: €${Number(price).toLocaleString('nl-NL', { maximumFractionDigits: 0 })}` : null,
      ].filter(Boolean).join(' · ');

      const head = lang === 'nl' ? headNL : headEN;
      const specs = lang === 'nl' ? specsNL : specsEN;
      const prefix = specs ? `${head}\n${specs}\n\n` : `${head}\n\n`;

      // only replace the prefix if we’re switching from empty or from a previous auto-prefix
      if (!m.value || m.dataset.autoprefill === '1') {
        m.value = prefix;
        m.dataset.autoprefill = '1';
      } else {
        // If user already typed, don't nuke their text. Prepend once instead.
        if (m.value.indexOf(head) !== 0) {
          m.value = `${prefix}${m.value}`;
        }
        m.dataset.autoprefill = '0';
      }
    }
  }

  function clearInterestContext() {
    setInterestContext(null);
  }

  // If language changes while the modal is open, re-localize the prefix
  function updateInterestContextLanguage() {
    if (!interestContext) return;
    setInterestContext({ ...interestContext }); // reapply with new lang
  }







  // translations
  const t = {
    nl: {
      status: {
        processing: "Je bericht wordt verzonden…",
        success: "Bedankt! Je bericht is verzonden.",
        error: "Verzenden mislukt. Probeer het later opnieuw."
      },
      interest: "Interesse tonen",
      modalTitle: "Interesse tonen",
      reset: "reset filters",
      noResults: "Geen resultaten",
      all: "all",
      send: "Versturen",
      matchingUnits: "gevonden units:",
      filtersTitle: "filters:",
      labels: {
        name: "Naam",
        email: "E-mail",
        phone: "Telefoon",
        message: "Bericht",
      },
      filterTitles: {
        type: "type",
        status: "status",
        area: "oppervlakte",
      },
      filterTypes: {
        horeca: "horeca",
        "living lab": "living lab",
        office: "kantoor",
      },
      filterStatus: {
        leased: "verhuurd",
        reserved: "gereserveerd",
        available: "beschikbaar",
      },
      details: {
        title: "geselecteerde unit:",
        name: "naam",
        type: "type",
        level: "niveau",
        availability: "beschikbaarheid",
        price: "prijs",
        area: "oppervlakte",
      },
      priceSuffix: { per_m2: "/ m²", per_unit: "/ unit" },
      alert: "Bedankt voor je interesse! We nemen spoedig contact met je op.",
    },
    en: {
      status: {
        processing: "Sending your message…",
        success: "Thanks! Your message has been sent.",
        error: "Failed to send. Please try again later."
      },
      interest: "I'm interested",
      modalTitle: "I'm interested",
      reset: "reset filters",
      noResults: "No results",
      all: "all",
      send: "Send",
      matchingUnits: "matching units:",
      filtersTitle: "filters:",
      labels: {
        name: "Name",
        email: "Email",
        phone: "Phone",
        message: "Message",
      },
      filterTitles: {
        type: "type",
        status: "status",
        area: "area",
      },
      filterTypes: {
        horeca: "horeca",
        "living lab": "living lab",
        office: "office",
      },
      filterStatus: {
        leased: "leased",
        reserved: "reserved",
        available: "available",
      },
      details: {
        title: "selected unit:",
        name: "name",
        type: "type",
        level: "level",
        availability: "availability",
        price: "price",
        area: "area",
      },
      priceSuffix: { per_m2: "/ m²", per_unit: "/ unit" },
      alert: "Thank you for your interest! We’ll contact you soon.",
    },
  };
  window.t = t;


  let currentLang = window.__pendingLang || localStorage.getItem("lang") || "nl";
  applyLang(currentLang);

  langToggle.addEventListener("click", () => {
    currentLang = currentLang === "nl" ? "en" : "nl";
    localStorage.setItem("lang", currentLang);
    applyLang(currentLang);
  });

  function applyLang(lang) {
    langToggle.textContent = lang.toUpperCase();
    unitsLayer?.refreshTooltipLanguage?.();

    // ─ UI basics ─
    if (interestedBtn) interestedBtn.textContent = t[lang].interest;
    if (modalTitle) modalTitle.textContent = t[lang].modalTitle;
    const submit = modal?.querySelector(".submit-btn");
    if (submit) submit.textContent = t[lang].send;

    // Ensure a persistent status element exists under the submit button
    if (submit && !form.querySelector('#form-status')) {
      const statusEl = document.createElement('div');
      statusEl.id = 'form-status';
      statusEl.setAttribute('aria-live', 'polite');  // announce updates
      statusEl.style.marginTop = '10px';
      statusEl.style.fontSize = '13px';
      submit.insertAdjacentElement('afterend', statusEl);
    }

    // ─ filter panel ─
    const filtersMainTitle = document.getElementById('filters-main-title');
    if (filtersMainTitle) filtersMainTitle.textContent = t[lang].filtersTitle;

    const titles = document.querySelectorAll("#filtering-system .filter-title:not(#filters-main-title)");
    if (titles[0]) titles[0].textContent = `▪ ${t[lang].filterTitles.type} ▪`;
    if (titles[1]) titles[1].textContent = `▪ ${t[lang].filterTitles.status} ▪`;
    if (titles[2]) titles[2].textContent = `▪ ${t[lang].filterTitles.area} ▪`;

    // ─ results panel title ─
    const resultsTitle = document.getElementById('results-title');
    if (resultsTitle) resultsTitle.textContent = t[lang].matchingUnits;

    const horecaLabel = document.querySelector('label[for="type-horeca"] .type-text');
    const livingLabLabel = document.querySelector('label[for="type-living-lab"] .type-text');
    const officeLabel = document.querySelector('label[for="type-office"] .type-text');
    if (horecaLabel) horecaLabel.textContent = t[lang].filterTypes.horeca;
    if (livingLabLabel) livingLabLabel.textContent = t[lang].filterTypes["living lab"];
    if (officeLabel) officeLabel.textContent = t[lang].filterTypes.office;

    const leasedLabel = document.querySelector('label[for="leased"]');
    const reservedLabel = document.querySelector('label[for="reserved"]');
    const availableLabel = document.querySelector('label[for="available"]');
    if (leasedLabel) leasedLabel.textContent = t[lang].filterStatus.leased;
    if (reservedLabel) reservedLabel.textContent = t[lang].filterStatus.reserved;
    if (availableLabel) availableLabel.textContent = t[lang].filterStatus.available;

    // ─ reset + results ─
    const resetEl = document.querySelector("#filter-reset .filter-reset");
    const showAll = document.querySelector("#level-show-all .show-all");
    if (resetEl) resetEl.textContent = t[lang].reset;
    if (showAll) showAll.textContent = t[lang].all;

    const noResults = document.querySelector("#filter-results .no-results");
    if (noResults) noResults.textContent = t[lang].noResults;

    // ─ form labels ─
    const nameLabel = form.querySelector('label[for="name"]');
    const emailLabel = form.querySelector('label[for="email"]');
    const phoneLabel = form.querySelector('label[for="phone"]');
    const messageLabel = form.querySelector('label[for="message"]');
    if (nameLabel) nameLabel.textContent = t[lang].labels.name;
    if (emailLabel) emailLabel.textContent = t[lang].labels.email;
    if (phoneLabel) phoneLabel.textContent = t[lang].labels.phone;
    if (messageLabel) messageLabel.textContent = t[lang].labels.message;

    // store current language globally
    window.currentLang = lang;

    refreshLanguageText();
    updateInterestContextLanguage();
  }



  // expose a safe global setter for external callers
  window.setAppLanguage = function setAppLanguage(lang) {
    // validate & normalize
    const code = String(lang || '').toLowerCase();
    const allowed = (code === 'nl' || code === 'en') ? code : 'nl';
    localStorage.setItem('lang', allowed);
    applyLang(allowed);
  };

  // if a language arrived before DOM was ready, apply it now
  if (window.__pendingLang) {
    window.setAppLanguage(window.__pendingLang);
    window.__pendingLang = null;
  } else {
    // your existing default init is fine:
    // currentLang = localStorage.getItem("lang") || "nl";
    // applyLang(currentLang);
  }

  function clearFormStatus() {
    const el = form.querySelector('#form-status');
    if (!el) return;
    el.textContent = '';
    el.classList.remove('is-processing', 'is-success', 'is-error');
  }
  // modal open/close
  interestedBtn?.addEventListener("click", () => {
    clearInterestContext?.();            // clear hidden fields / context
    clearFormStatus();

    // ALSO clear the message box so it's a fresh, generic inquiry
    const m = form.querySelector('#message');
    if (m) {
      m.value = '';
      m.dataset.autoprefill = '0';
    }

    modal.classList.remove("hidden");
  });


  closeModalBtn.addEventListener("click", () => {
    clearFormStatus();
    modal.classList.add("hidden");
  });

  modal.addEventListener("click", (e) => {
    if (e.target === modal) {
      clearFormStatus();
      modal.classList.add("hidden");
    }
  });
  // STEP 4 (B): unit-specific Interested button inside the Unit Details panel
  document.getElementById('details-display')?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-role="interested-unit"]');
    if (!btn) return;

    // Pull currently selected unit from the 3D layer
    const sel = (unitsLayer && unitsLayer._selected && unitsLayer._selected.values().next().value) || null;
    const data = sel?.unitData || null;
    const unitNumber = sel?.unitNumber ?? data?.number ?? null;
    const level = (data?.level != null ? data.level : sel?.level);

    if (!data || !unitNumber) {
      // Fallback: open generic modal if something’s off
      clearInterestContext?.();
      modal.classList.remove("hidden");
      return;
    }

    const lang = window.currentLang || 'nl';
    const unitName = localizedName(data, lang);

    setInterestContext?.({
      unitNumber,
      level,
      unitName,
      area: (typeof data.area === 'number') ? data.area : parseFloat(data.area),
      price: (data.price != null && data.price !== '') ? Number(data.price) : null,
      type: data.type || null
    });

    modal.classList.remove("hidden");
  });


  form.addEventListener("submit", async (e) => {
    e.preventDefault();

    const nameEl = form.querySelector('#name');
    const emailEl = form.querySelector('#email');
    const phoneEl = form.querySelector('#phone');
    const msgEl = form.querySelector('#message');

    const payload = {
      name: nameEl?.value?.trim() || '',
      email: emailEl?.value?.trim() || '',
      phone: phoneEl?.value?.trim() || '',
      message: msgEl?.value || '',
      unit: hidUnit.value || null,
      level: hidLevel.value || null,
      unitName: hidUName.value || null,
      language: hidLang.value || (window.currentLang || 'nl'),
    };

    try {
      setFormEnabled(false);
      setFormStatus('processing', window.currentLang || 'nl');

      const res = await fetch(INTEREST_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      // confirm success via HTTP + JSON {ok:true}
      let data = null;
      try { data = await res.json(); } catch { }
      if (!res.ok || (data && data.ok === false)) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      setFormStatus('success', window.currentLang || 'nl');
      form.reset();
      msgEl.dataset.autoprefill = '0';
      clearInterestContext();
      // keep modal open so user sees the message
      // setTimeout(() => modal.classList.add("hidden"), 1500); // optional auto-close
    } catch (err) {
      console.error('Interest submit failed:', err);
      setFormStatus('error', window.currentLang || 'nl');
    } finally {
      setFormEnabled(true);
    }
  });


});



